// @flow
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import SortableList, {SortableItem, SortableKnob} from 'react-easy-sort';
import {makeStyles} from '@material-ui/core/styles';
import {
  Typography,
  Hidden,
  IconButton,
  FormControlLabel,
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Divider,
  Grid,
  Collapse,
  ButtonBase,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import {
  AddCircleTwoTone,
  Close,
  DeleteOutline,
  Info,
  Publish,
  Crop,
  ExpandMore,
  Lock,
  InfoOutlined,
  DragHandle,
} from '@material-ui/icons';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';
import {getImageUrl} from '../../../utils/image_provider';
import {getBasicDomain, useIsFeatureInTier} from '../../../utils/component';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';
import {createFile} from '../../../utils/image';
import CustomTextField from '../../../elements/CustomTextField/CustomTextField';
import {mapProductOptionSettingToRequest} from '../utils/productOptionMapper';
import {getFilledValues} from '../../../utils/form';
import ChooseVariantImageModal from './ChooseVariantImageModal/ChooseVariantImageModal';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import TierRestrictionModal from '../../../elements/TierRestrictionModal/TierRestrictionModal';

import analytics from '../../../analytics';
import ProductFeaturesSection from './Sections/ProductFeaturesSection/ProductFeaturesSection';

import type {
  ProductRequest,
  ProductVariantsRequest,
  ProductOptionRequest,
} from '../api';
import VariantBulkPriceAlertModal from './VariantBulkPriceAlertModal/VariantBulkPriceAlertModal';
import PremiumFeatureBadge from '../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import LoadingSpinner from '../../../elements/LoadingSpinner/LoadingSpinner';
import CustomExitPrompt from '../../../elements/CustomExitPrompt/CustomExitPrompt';
import {scrollTop} from '../../../utils/dom';
import queryString from 'query-string';
import ExpandLess from '@material-ui/icons/ExpandLess';
import {getProductTaxesService, getTaxesService} from '../promises';
import CropModal from '../../../elements/CropModal';

import invoicingDesktop from '../../../assets/img/minimal/invoicing.webp';
import invoicingMobile from '../../../assets/img/minimal/invoicing-mobile.webp';

import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {actions as ProductActions} from '../duck';

import type {Node} from 'react';
import type {RootState} from '../../index';
import type {Dispatch} from 'redux';
import type {
  ProductDetail,
  Product as ProductType,
  ProductVideo,
  Currency,
  Store,
  Tax,
  ProductMetaDataSetting,
  ProductSpecificationSetting,
  ProductSizeChartSetting,
  RelatedProductsSetting,
  ProductVariantOption,
  ProductVariantOptionValue,
} from '../../../entities';
import {useToggle, classNames} from '../../../utils/react';
import Seo from './ProductTabs/Seo/Seo';
import {useProductFormContext} from '../context/ProductForm';
import RelatedProducts from './ProductTabs/RelatedProducts/RelatedProducts';
import TaxesSection from './Sections/TaxesSection/TaxesSection';
import Options from './ProductTabs/Options/Options';
import Sections from './ProductTabs/Sections/Sections';
import ProductVideoSection from './Sections/ProductVideoSection';

import TabWrapper from './ProductTabs/TabWrapper/TabWrapper';
import {useTypedSelector} from '../../../store';

import {
  useIsDowngradeTier,
  useIsMinimalPlan,
  useStoreMetadata,
  useStoreBusinessModel,
} from '../../store/utils';
import {useTaxesSetting} from '../../configure/taxes/hook';
// import {MAP_FIELDS_NAME} from '../constants';

import ResumeTaxModal from './Modals/ResumeTaxModal';

import type {ImageToCrop} from '../../../elements/CropModal';
import useFormTabErrors from '../hooks/useFormTabErrors';
import {FIELDS_BY_TAB_NAME} from '../constants';
import {useAlert} from '../../../elements/Alert/AlertContext';
import StoreChurnModal from '../../checkout/components/Modals/StoreChurnModal';
import {arrayMove} from '../../../utils/array';
import {useProductTax} from '../utils/hooks';
import VariantsSection from './ProductTabs/ProductVariants';
import {getOriginalFileUrl} from '../../file-storage/utils';
import TiendadaTooltip from '../../../elements/tiendada-tooltip';

import type {ProductOptionSetting} from '../../../entities/product-option';
import {SUBCATEGORY_ITEM_ENABLED} from '../../categories/components/Subcategory/SubcategoryList';
import ProductFormCategory from './productFormCategory/ProductFormCategory';

export default connect(
  (state: RootState) => ({
    store: state.store.store,
    user: state.auth.user,
    currency: state.currency.currency,
    isSaving: state.products.isSaving,
    isFetching: state.products.isFetching,
    createProductSuccess: state.products.createProductSuccess,
    editProductSuccess: state.products.editProductSuccess,
    // information: state.ui.information,
    storeInformation: state.ui.storeInformation,
    resellerInformation: state.ui.resellerInformation,
    deleteProductSuccess: state.products.deleteProductSuccess,
    createFinalSettingProductSuccess:
      state.products.createFinalSettingProductSuccess,
    featuredProducts: state.products.featuredProducts,
    productImageShape:
      (state.ui.storeInformation &&
        state.ui.storeInformation.imageSetting &&
        state.ui.storeInformation.imageSetting.productImageShapeType) ||
      'SQUARE',
    productMetadata: state.products.productMetadata,
    productSpecification: state.products.productSpecification,
    relatedProducts: state.products.relatedProducts,
    productSizeChart: state.products.productSizeChart,
  }),
  (dispatch: Dispatch<any>) =>
    bindActionCreators(
      {
        ...ProductActions,
      },
      dispatch
    )
)(ProductForm);

type Props = {
  store?: Store,
  product?: ProductDetail,
  productMetadata?: ProductMetaDataSetting,
  productSpecification?: ProductSpecificationSetting,
  relatedProducts?: RelatedProductsSetting,
  productSizeChart?: ProductSizeChartSetting,
  isFetching: boolean,
  isSaving: boolean,
  currency?: Currency,
  createProduct: ({
    productRequest: ProductRequest,
    productVariantsRequest?: ProductVariantsRequest,
  }) => void,
  createProductSuccess?: string | boolean,
  editProduct: ({
    productRequest: ProductRequest,
    productVariantsRequest?: ProductVariantsRequest,
  }) => void,
  editProductSuccess?: boolean,
  defaultImages?: string[],
  defaultName?: string,
  defaultDescription?: string,
  deleteProductSuccess?: boolean,
  createFinalSettingProductSuccess?: boolean,
  savedCallback?: () => void,
  isInstagramUpload?: boolean,
  instagramUploadEarlyExit?: () => void,
  featuredProducts?: ProductType[],
  productImageShape?: string,
  fetchProductSpecifications: ({productId: string}) => void,
  fetchRelatedProducts: ({productId: string}) => void,
  fetchProductSizeChart: ({productId: string}) => void,
  fetchProductMetadata: ({productId: string}) => void,
  fetchProductVariantOptions: () => void,
  children?: Node,
  tabSelected: number,
  isDuplicating?: boolean,
  fetchProduct: ({productId: string}) => void,
};

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1300,
    // overflowY: 'auto',
    // height: '100%',
    // background: '#f8f9fb',
    // overflow: 'auto',
    marginBottom: 180,
    padding: theme.spacing(0, 1, 1, 1),
    [theme.breakpoints.only('sm')]: {
      marginBottom: 144,
    },
    [theme.breakpoints.up('sm')]: {
      marginBottom: 80,
      padding: theme.spacing(0, 2, 2, 2),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 4, 4, 4),
    },
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    // alignItems: 'center',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(0, 1, 1, 1),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 3, 3, 3),
    },
    background: 'white',
  },
  desktopColumns: {
    display: 'flex',
    // gap: theme.spacing(2),
    '& > :not(:first-child)': {
      marginLeft: theme.spacing(2),
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      '& > :not(:first-child)': {
        marginLeft: 0,
      },
    },
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  sectionCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: colors.grey700,
      fontSize: '0.875rem',
    },
  },
  imagesContainer: {
    display: 'flex',
    maxWidth: '100%',
    overflowX: 'auto',
    gap: '.5rem',
    minHeight: 120,
  },
  productImageContainer: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  dragHandle: {
    cursor: 'grab',
    width: '100%',
    display: 'flex',
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageBox: {
    display: 'flex',
    height: 120,
    width: 120,
    minWidth: 120,
    justifyContent: 'center',
    alignSelf: 'center',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    border: `2px dashed ${colors.grey500}`,
    overflow: 'hidden',
    borderRadius: '12px',
  },
  mainProductImage: {
    border: `2px solid ${colors.purple3}`,
    position: 'relative',
    '&::after': {
      content: '"PORTADA"',
      position: 'absolute',
      color: 'white',
      bottom: 0,
      left: 0,
      width: '100%',
      textAlign: 'center',
      background: colors.purple3,
      // borderRadius: `0 0 3px 3px`,
      padding: 6,
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  addImageBox: {
    opacity: 0.8,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '.25rem',

    '&:hover': {
      opacity: 1,
      backgroundColor: colors.grey200,
    },
  },
  editImage: {
    '&:hover': {
      cursor: 'pointer',
    },
    '&:hover:after': {
      content: '"PORTADA"',
      position: 'absolute',
      color: 'white',
      bottom: 0,
      left: 0,
      width: '100%',
      background: colors.purple3,
      padding: 6,
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  imageBoxDisabled: {
    filter: 'grayscale(1)',
    opacity: 0.7,
    cursor: 'default',
    '&:hover': {
      opacity: 0.7,
      backgroundColor: 'initial',
    },
  },
  uploadIcon: {
    color: colors.purple3,
    backgroundColor: colors.lightPurple,
    borderRadius: 40,
  },
  secondaryImages: {
    border: `2px solid ${colors.grey300}`,
  },
  image: {
    // width: '100%',
    height: 'auto',
    // borderRadius: theme.shape.borderRadius,
    /*boxShadow: theme.shadows[2],*/
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
  },
  removeImageIcon: {
    position: 'absolute',
    top: '2rem',
    right: 4,
    padding: 4,
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: colors.grey300,
    },
  },
  upsellRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  upsellMessage: {
    width: 'fit-content',
    backgroundColor: colors.lightPurple,
    color: colors.purple3,
    fontSize: '0.875rem',
    borderRadius: '12px',
    padding: theme.spacing(1, 2),
  },
  productInputField: {
    marginTop: theme.spacing(2),
  },
  variantsActivatedPricesAlert: {
    marginTop: theme.spacing(1),
    color: colors.grey700,
    fontSize: '0.875rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
  },
  bulkPricesSubtitle: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  bulkPriceCheckbox: {
    '& span': {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  quantityInputField: {
    [theme.breakpoints.up('md')]: {
      maxWidth: 200,
    },
    '& input:disabled': {
      borderRadius: theme.borderRadius,
      backgroundColor: '#F4F4F6',
    },
  },
  bulkPriceInputField: {
    width: '90%',
    '& input:disabled': {
      borderRadius: theme.borderRadius,
      backgroundColor: '#F4F4F6',
    },
  },
  quantityLastInputField: {
    border: 'none',
    backgroundColor: 'none',
    textAlign: 'center',
    '& input': {
      border: 'none',
      backgroundColor: 'none',
      textAlign: 'center',
    },
  },
  addLineButton: {
    alignSelf: 'flex-start',
    color: colors.purple3,
  },
  moreContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItem: 'center',
  },
  more: {
    color: colors.grey700,
  },
  table: {
    marginTop: theme.spacing(2),
  },
  deleteIcon: {
    borderRadius: '8px',
    color: '#E54D7B',
    minWidth: 40,
  },
  deleteIconCell: {},
  errorRow: {
    border: 'none',
    padding: '0 0 0 4px',
    '& p': {
      padding: 0,
      margin: 0,
      color: 'red',
    },
  },
  premiumLabel: {
    color: 'white',
    fontWeight: 600,
    background: colors.purple3,
  },
  categories: {
    marginTop: theme.spacing(1),
  },
  optionsTitle: {
    marginTop: theme.spacing(2),
    fontWeight: 500,
    color: colors.grey700,
  },
  criteriaHeader: {
    display: 'flex',
    alignItems: 'center',
    // marginTop: theme.spacing(2),
    justifyContent: 'space-between',
  },
  variantInstructions: {
    maxWidth: 500,
    marginBottom: 8,
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  subtitle2: {
    marginLeft: 0,
    fontSize: '0.875rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
  infoBox: {
    margin: theme.spacing(1, 0),
    borderRadius: '8px',
    backgroundColor: colors.grey200,
    padding: theme.spacing(0, 2, 2, 2),
  },
  infoBoxText: {
    fontSize: '0.75rem',
  },
  selectWrapper: {
    marginTop: theme.spacing(2),
  },
  select: {
    borderRadius: '8px',
  },
  noMarginTop: {
    marginTop: 'unset',
  },
  autocompleteInputRoot: {
    flexWrap: 'wrap',
  },
  variantsTable: {
    margin: theme.spacing(0, -3),
    overflowX: 'auto',
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2, -2),
    },
  },
  variantsTableImage: {
    display: 'flex',
    height: 56,
    width: 56,
    justifyContent: 'center',
    alignSelf: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRadius: '12px',
    cursor: 'pointer',
    marginRight: theme.spacing(2),
    overflow: 'hidden',
    // objectFit: 'cover',
  },
  variantsTableInputField: {
    width: 120,

    '& input::-webkit-outer-spin-button': {
      display: 'none',
    },
    '& input::-webkit-inner-spin-button': {
      display: 'none',
    },
  },
  variantsRow: {
    '& td': {
      padding: '.75rem .35rem',
    },
  },
  variantsRowDisabled: {
    backgroundColor: colors.grey200,
  },
  bottomBar: {
    zIndex: 1200, // same as sidebar
    position: 'fixed',
    bottom: 0,
    width: 'calc(100% - 264px)',
    backgroundColor: 'white',
    boxShadow: '0px -4px 12px rgba(222, 222, 222, 0.25)',
    [theme.breakpoints.down('sm')]: {
      bottom: 64,
      width: '100%',
    },
  },
  bottomBarContent: {
    maxWidth: 1300,
    padding: theme.spacing(2, 4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bottomBarText: {
    fontWeight: 600,
  },
  bottomBarButton: {
    height: 48,
    fontSize: '1.125rem',
  },
  mobileBottomButton: {
    zIndex: 1,
    position: 'fixed',
    bottom: 64,
    height: 48,
    margin: theme.spacing(2),
    width: 'calc(100% - 32px)',
    fontSize: '1.125rem',
  },
  chip: {
    marginTop: '22px',
  },
  updateCoverImageCrop: {
    position: 'absolute',
    top: 4,
    left: 4,
    padding: 4,
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: colors.grey300,
    },
  },
  updateImageCrop: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    padding: 4,
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: colors.grey300,
    },
  },
  textHelper: {
    color: '#717190',
    fontSize: '14px',
    marginTop: theme.spacing(1),
    [theme.breakpoints.up('lg')]: {
      marginTop: theme.spacing(3),
      marginLeft: '10px',
    },
  },
  sectionTitleStock: {
    display: 'flex',
    flexDirection: 'column',
  },
  wrapperStock: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '0.5rem',
    alignItems: 'flex-start',
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  onlyTiendadaProSection: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
      justifyContent: 'right',
    },
  },
  lockIcon: {
    color: colors.grey600,
  },
  onlyTiendadaPro: {
    marginLeft: theme.spacing(1),
    color: colors.grey900,
    fontWeight: 600,
  },
  mejorarPlanButton: {
    marginLeft: theme.spacing(2),
    padding: '4px 14px',
    background: colors.lightPurple,
  },
  premiumFeaturesNameSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  premiumFeaturesName: {
    margin: theme.spacing(1, 5, 1, 1),
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey800,
  },
  premiumFeaturesDescription: {
    margin: theme.spacing(0, 5, 1, 1),
    fontSize: '0.875rem',
    fontWeight: 400,
    color: colors.grey700,
  },
  collapsibleInput: {
    borderTop: '1px solid #E8E8ED',
    borderBottom: '1px solid #E8E8ED',
    borderRadius: '0!important',
    boxShadow: 'none!important',
    '& .MuiTypography-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
    '& .MuiCollapse-wrapperInner > div': {
      padding: 0,
    },
  },
  tooltip: {
    marginLeft: theme.spacing(2),
    backgroundColor: '#F4F4F6',
    borderRadius: '8px',
    textAlign: 'center',
    maxWidth: 240,
  },
  categoryTooltip: {
    backgroundColor: colors.grey500,
  },
  textTooltip: {
    textAlign: 'left!important',
    color: ' #717190',
    fontSize: '10px',
    '& span': {
      marginLeft: theme.spacing(1),
    },
  },
  infoBtn: {
    color: '#9595AB',
  },
  sectionTitleSubcategory: {
    fontSize: '14px',
    fontWeight: 600,
    margin: theme.spacing(1, 0),
    color: colors.grey900,
    textTransform: 'capitalize',
  },
  spaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  productFeatureLabel: {
    color: colors.grey700,
    fontSize: 14,
    fontWeight: 400,
    '& span': {
      color: colors.grey900,
      fontWeight: 500,
    },
  },
  categoryHeader: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    '& > p': {
      marginTop: 0,
    },
  },
  variantMenuItem: {
    boxSizing: 'border-box',
    margin: theme.spacing(0, 1),
    borderRadius: '6px',
    maxWidth: '100%',
  },
  variantNameInputWrap: {
    width: '100%!important',
    backgroundColor: '#FFFFFF!important',
    position: 'absolute',
    top: 221,
    height: '100%',
    left: 0,
    zIndex: 20,
    border: `1px solid ${colors.grey300}`,
  },
  wrapVariants: {
    padding: theme.spacing(1),
    border: `1px solid ${colors.grey300}`,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    width: '100%',
    height: '100%',
    borderRadius: '10px',
    position: 'relative',
  },
  editVariantOptionsBtn: {
    position: 'absolute',
    top: 12,
    right: 8,
  },
  variantChip: {},
  invoicingImage: {
    maxWidth: '100%',
    width: '100%',
    margin: '1rem 0 3rem 0',
    display: 'block',
  },
  addVariantOption: {
    display: 'flex',
    alignItems: 'center',
    color: colors.purple,
    gap: '8px',
    textDecoration: 'none',
    height: '100%',
  },
  tabErrors: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tabErrorsName: {
    color: colors.grey800,
    fontSize: 14,
  },
  tabErrorsIcon: {
    color: '#E13065',
  },
  showLabelContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
}));
type SavedImage = {
  id: string,
  url: string,
};

type ProductCriteria = {
  criterion: ProductVariantOption,
  variants: ProductVariantOptionValue[],
};

// const criteria = ['Presentación', 'Tamaño', 'Color', 'Talla', 'Tipo'];

const SIZE_RECOMMENDATION = {
  SQUARE: {
    width: 1080,
    height: 1080,
  },
  RECTANGLE: {
    width: 1080,
    height: 1440,
  },
};

function ProductForm(props: Props) {
  const {
    store,
    product,
    isFetching,
    isSaving,
    currency,
    createProduct,
    createProductSuccess,
    editProduct,
    editProductSuccess,
    defaultImages,
    // defaultName,
    defaultDescription,
    deleteProductSuccess,
    createFinalSettingProductSuccess,
    savedCallback,
    isInstagramUpload,
    instagramUploadEarlyExit,
    featuredProducts,
    productImageShape: productImageShapeProp = 'SQUARE',
    productMetadata,
    productSpecification,
    relatedProducts,
    fetchProductMetadata,
    fetchProductSpecifications,
    fetchRelatedProducts,
    fetchProductSizeChart,
    productSizeChart,
    tabSelected,
    fetchProductVariantOptions,
    fetchProduct,
    isDuplicating,
  } = props;

  // const isFromExternalResource = !!product?.metadataSetting?.source;
  const isFromExternalResource = false;

  const {
    enabledSeo,
    setEnabledSeo,
    //size guide
    sizeGuideSelected,
    setSizeGuideSelected,
    enabledSizeGuide,
    setEnabledSizeGuide,
    // related
    enabledRelatedProducts,
    setEnabledRelatedProducts,
    relatedProductsOrigin,
    setRelatedProductsOrigin,
    listProductsItems,
    setListProductsItems,
    listCategoryItems,
    setListCategoryItems,
    listSubcategoriesItems,
    setListSubcategoriesItems,
    setListItems,
    //options

    //sections,
    sectionImages,
    sectionImageLayoutType,
  } = useProductFormContext();

  const {showAlert} = useAlert();

  const classes = useStyles();

  const {productId} = useParams();
  const queryObject = queryString.parse(window.location.search);

  const {enabledTaxInclusivePricing, isFetchingTaxSetting} = useTaxesSetting();
  const hasMultipleImagesFeature = useIsFeatureInTier('VARIOUS_PRODUCT_IMAGES');
  const hasImageShapeFeature = useIsFeatureInTier('IMAGE_CONFIGURATION');
  const hasProductOptionsFeature = useIsFeatureInTier('PRODUCT_OPTIONS');

  const productImageShape = hasImageShapeFeature
    ? productImageShapeProp
    : 'SQUARE';

  const history = useHistory();

  const [missingImage, setMissingImage] = useState(false);
  const categoryValuesRef = useRef({
    categories: [],
    subCategories: [],
    subCategoryItems: [],
  });

  const [selectedCategories, setSelectedCategories] = useState([]);

  const [
    showVariantBulkPriceAlertModal,
    toggleShowVariantBulkPriceAlertModal,
  ] = useToggle();

  const {tabWithErrors} = useFormTabErrors(FIELDS_BY_TAB_NAME);

  const storeTierName = store?.storeTier?.name || '';

  const mainForm = useFormContext();
  const hasVariantsNewImagesAlertRef = useRef(false);

  const {
    register,
    handleSubmit,
    errors,
    setError,
    clearErrors,
    formState,
    getValues,
    setValue,
    control,
    watch,
  } = mainForm;

  const {dirtyFields} = formState;

  const {
    fields: bulkPriceFields,
    append: appendBulkPrice,
    prepend: prependBulkPrice,
    remove: removeBulkPrice,
  } = useFieldArray({
    control,
    name: 'bulkPrices',
  });

  useEffect(() => {
    register('productFeatures');
  }, [register]);

  const sizeCharts = useTypedSelector(
    state =>
      state.productPage?.productPageSetting?.storeSizeChartSetting?.sizeCharts
  );

  const isDowngradeTier = useIsDowngradeTier();
  const isMinimalPlan = useIsMinimalPlan();

  const [orderTaxes, setOrderTaxes] = useState<Tax[]>([]);
  const [innerProductTaxes, setInnerProductTaxes] = useState<Tax[]>([]);
  const [categoryTaxes, setCategoryTaxes] = useState<Tax[]>([]);
  const [storeTaxes, setStoreTaxes] = useState<Tax[]>([]);

  const {setProductTax, loading} = useProductTax();

  const [productDescription, setProductDescription] = useState('');
  const [bulkPriceShow, setBulkPriceShow] = useState(false);

  const [priceDisplayMode, setPriceDisplayMode] = useState('SHOW_PRICE');

  const productVideosRef = useRef<ProductVideo[]>([]);

  const [imagesToCrop, setImagesToCrop] = useState<ImageToCrop[]>([]);
  const [cropResult, setCropResult] = useState<ImageToCrop[]>([]);
  const [savedImages, setSavedImages] = useState<SavedImage[]>([]);

  const imageIndexToCropRef = useRef<number | null>(null);

  // const [enabledSizeGuide, setEnabledSizeGuide] = useState<boolean>(false);
  const [enabledProductFeatures, setEnabledProductFeatures] = useState<boolean>(
    false
  );

  const hasTaxesFeature = useIsFeatureInTier('TAXES');
  const hasProductSizeChartFeature = useIsFeatureInTier('PRODUCT_SIZE_CHART');
  const hasProductMetadataFeature = useIsFeatureInTier('PRODUCT_SEO');
  const hasRelatedProductFeature = useIsFeatureInTier('RELATED_PRODUCTS');
  const hasProductFeaturesFeature = useIsFeatureInTier(
    'PRODUCT_SPECIFICATIONS'
  );

  const productOptionSettingRef = useRef<ProductOptionSetting | null>(null);

  const handleChangeProductOptionSetting = useCallback(
    (setting: ProductOptionSetting) => {
      productOptionSettingRef.current = setting;
    },
    []
  );

  const storeMetadata = useStoreMetadata();

  const bulkPriceDisabled = storeMetadata
    ? !storeMetadata?.featureRestrictions?.product?.BULK_PRICES
    : false;

  const handleFileInputChange = async (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const {target} = event;
    if (!target) return;
    imageIndexToCropRef.current = null;

    const files = Array.from(target.files);
    if (files.length === 0) {
      setMissingImage(true);
      return;
    }

    const newImages: ImageToCrop[] = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImagesToCrop(newImages);

    event.target.value = '';
  };

  const handleChangeProductTax = (taxId: string) => {
    if (!product) return;
    setProductTax(taxId, product.id)
      .then(() => {
        showAlert('Se ha actualizado el impuesto del producto', {
          type: 'success',
          duration: 4000,
          clearPreviousAlerts: true,
        });
      })
      .catch(() => {
        showAlert('No se ha podido actualizar el impuesto del producto', {
          type: 'error',
          duration: 4000,
          clearPreviousAlerts: true,
        });
      });
  };

  useEffect(() => {
    if (isInstagramUpload && defaultDescription !== undefined) {
      setProductDescription(defaultDescription);
    }
  }, [isInstagramUpload, defaultDescription]);

  useEffect(() => {
    if (!defaultImages || defaultImages.length === 0) return;

    defaultImages.forEach(defaultImage =>
      createFile(defaultImage).then(({file}) => {
        const newImage = {
          file,
          preview: defaultImage,
        };

        setImagesToCrop(prev => [...prev, newImage]);
      })
    );
  }, [defaultImages]);

  useEffect(() => {
    setMissingImage(false);

    setImagesToCrop([]);
    setSavedImages([]);
    setCropResult([]);
  }, []);

  useEffect(() => {
    if (product && product.images && product.images.length) {
      const sortedProducts = [...product.images]
        .sort((a, b) => a.number - b.number)
        .map(image => {
          return {
            id: image.id,
            url: getImageUrl(image.id),
          };
        });
      setSavedImages(sortedProducts);
      setImagesToCrop([]);
      setCropResult([]);
    }
  }, [product]);

  useEffect(() => {
    if (product) {
      if (product.description) setProductDescription(product.description);
      if (product.metadataSetting?.priceDisplay?.mode) {
        setPriceDisplayMode(
          product.metadataSetting?.priceDisplay.mode === 'PRICE'
            ? 'SHOW_PRICE'
            : 'SHOW_LABEL'
        );
      }
    }
  }, [product]);

  useEffect(() => {
    if (product && product.videos) {
      productVideosRef.current = product.videos.map(video => ({
        url: video.url,
        socialMediaType: video.socialMediaType,
      }));
    }
  }, [product]);

  useEffect(() => {
    if (product && hasTaxesFeature) {
      getProductTaxesService(product.id)
        .then(setInnerProductTaxes)
        .catch(e => {
          setInnerProductTaxes([]);
          console.log(`Error: ${e}`);
        });
    }
  }, [hasTaxesFeature, product]);

  useEffect(() => {
    if (hasTaxesFeature && productId === 'new') {
      getTaxesService({
        tax_criteria: 'ORDER',
        enabled: 1,
      })
        .then(setOrderTaxes)
        .catch(e => {
          setOrderTaxes([]);
          console.log(`Error: ${e}`);
        });
    }
  }, [hasTaxesFeature, productId]);

  useEffect(() => {
    if (hasTaxesFeature) {
      getTaxesService({enabled: 1})
        .then(setStoreTaxes)
        .catch(e => {
          setStoreTaxes([]);
          console.log(`Error: ${e}`);
        });
    }
  }, [hasTaxesFeature]);

  useEffect(() => {
    if (hasTaxesFeature) {
      //Exclude product categories, only call when is an attempt of new categories
      const realCategories = product ? product.categories : [];
      const onlyCategories = selectedCategories.filter(
        dc => !realCategories.find(c => c.id === dc.id)
      );
      if (onlyCategories.length) {
        const mapped = onlyCategories.map(el => el.id);
        getTaxesService({
          product_categories: mapped.join(','),
          enabled: 1,
        })
          .then(setCategoryTaxes)
          .catch(e => {
            setCategoryTaxes([]);
            console.log(`Error: ${e}`);
          });
      } else setCategoryTaxes([]);
    }
  }, [hasTaxesFeature, product, selectedCategories]);

  const totalProductTaxesFactor = useMemo(() => {
    const baseTaxes = orderTaxes.reduce(
      (acc, curr) => acc + parseFloat(curr.percentage),
      0
    );
    const innerTaxes = innerProductTaxes.reduce(
      (acc, curr) => acc + parseFloat(curr.percentage),
      0
    );
    const productCategoryTaxes = categoryTaxes.reduce(
      (acc, curr) => acc + parseFloat(curr.percentage),
      0
    );

    const total =
      1 +
      parseFloat(baseTaxes) +
      parseFloat(innerTaxes) +
      parseFloat(productCategoryTaxes);

    return parseFloat(total.toFixed(5));
  }, [categoryTaxes, innerProductTaxes, orderTaxes]);

  const calcPriceWithTax = useCallback(
    (value: number) => {
      return parseFloat(value * totalProductTaxesFactor).toFixed(5);
    },
    [totalProductTaxesFactor]
  );

  const calcPriceWithoutTax = useCallback(
    (value: number) => {
      return parseFloat(value / totalProductTaxesFactor).toFixed(5);
    },
    [totalProductTaxesFactor]
  );

  const getBulkPriceMaxValue = (index: number) => {
    const watchedValue = watch(`bulkPriceLine.${index + 1}.minQuantity`);

    if (watchedValue && typeof parseInt(watchedValue) === 'number')
      return parseInt(watchedValue) - 1;
    else return '';
  };

  const validateBulkPriceLineUnitPrice = (value: string, index: number) => {
    const price = getValues('taxedPrice');
    const offerPrice = getValues('taxedOfferPrice');

    const bulkPriceUnitPrice = parseFloat(value);
    if (offerPrice) {
      if (bulkPriceUnitPrice >= parseFloat(offerPrice))
        return 'El precio unitario debe ser menor que el precio de oferta del producto';
    }

    if (price) {
      if (bulkPriceUnitPrice >= parseFloat(price))
        return 'El precio unitario debe ser menor que el precio de venta del producto';
    } else return true;
  };

  const hasBulkPriceFeature = useIsFeatureInTier('BULK_PRICE');

  const handleBulkPriceShow = e => {
    if (!hasBulkPriceFeature) {
      setTierRestrictionModal(true);
    } else setBulkPriceShow(e.target.checked);
  };
  const handleAddLine = () => {
    appendBulkPrice({
      minQuantity: '',
      unitPrice: '',
      taxedUnitPrice: '',
    });
  };
  const handleRemoveLine = index => {
    const actualLength = bulkPriceFields.length;
    if (actualLength === 1) {
      setBulkPriceShow(false);
    }
    removeBulkPrice(index);
  };

  const hasVariousProductImagesFeature = useIsFeatureInTier(
    'VARIOUS_PRODUCT_IMAGES'
  );

  const hasSortProducImagesFeature = useIsFeatureInTier('SORT_PRODUCT_IMAGES');

  const uploadDisabled = useMemo(() => {
    if (!hasVariousProductImagesFeature)
      return savedImages && savedImages.length >= 1;

    return false;
  }, [hasVariousProductImagesFeature, savedImages]);

  const calcProductBasePrice = useCallback(
    (product: ProductDetail) => {
      if (enabledTaxInclusivePricing) {
        return calcPriceWithoutTax(parseFloat(product.price));
      } else return product.price;
    },
    [calcPriceWithoutTax, enabledTaxInclusivePricing]
  );

  // const calcProductBaseOfferPrice = useCallback(
  //   (product: ProductDetail) => {
  //     if (enabledTaxInclusivePricing) {
  //       return calcPriceWithoutTax(parseFloat(product.price));
  //     } else return product.price;
  //   },
  //   [calcPriceWithoutTax, enabledTaxInclusivePricing]
  // );

  useEffect(() => {
    if (hasProductMetadataFeature && product)
      fetchProductMetadata({productId: product.id});
  }, [fetchProductMetadata, hasProductMetadataFeature, product]);

  useEffect(() => {
    if (hasProductFeaturesFeature && product)
      fetchProductSpecifications({productId: product.id});
  }, [fetchProductSpecifications, hasProductFeaturesFeature, product]);

  useEffect(() => {
    if (hasRelatedProductFeature && product)
      fetchRelatedProducts({productId: product.id});
  }, [fetchRelatedProducts, hasRelatedProductFeature, product]);

  useEffect(() => {
    if (hasProductSizeChartFeature && product) {
      fetchProductSizeChart({productId: product.id});
    }
  }, [fetchProductSizeChart, hasProductSizeChartFeature, product]);

  useEffect(() => {
    if (productSizeChart && productSizeChart.sizeChart) {
      const id = productSizeChart.sizeChart.id;
      setEnabledSizeGuide(productSizeChart.enabled);
      setSizeGuideSelected(id);
    }
  }, [
    productMetadata,
    productSizeChart,
    setEnabledSizeGuide,
    setSizeGuideSelected,
  ]);

  useEffect(() => {
    if (productMetadata) {
      setEnabledSeo(productMetadata.enabled);
      setValue('metaTitle', productMetadata.title);
      setValue('metaDescription', productMetadata.description);
    }
  }, [productMetadata, setEnabledSeo, setValue]);

  const initialProductFeatures = useMemo(() => {
    if (!hasProductFeaturesFeature) return [];
    if (productSpecification && productSpecification.specifications) {
      return productSpecification.specifications.map(({name, description}) => ({
        name: name || '',
        description: description || '',
        id:
          Date.now().toString() +
          Math.random()
            .toString(36)
            .substring(2, 15),
      }));
    }
    return [];
  }, [hasProductFeaturesFeature, productSpecification]);

  useEffect(() => {
    if (relatedProducts) {
      const criteria = relatedProducts.relatedProductCriteria;
      setEnabledRelatedProducts(relatedProducts.enabled);

      const cleanProductItems = relatedProducts.relatedProducts
        ? relatedProducts.relatedProducts.map(product => ({
            id: product.id,
            name: product.name,
            mainImage: {id: product.mainImage ? product.mainImage.id : ''},
          }))
        : [];
      setListProductsItems(cleanProductItems);
      const cleanCategoryItems = relatedProducts.relatedProductCategories
        ? relatedProducts.relatedProductCategories.map(category => ({
            id: category.id,
            name: category.name,
            mainImage: {id: category.portraitImageId},
          }))
        : [];
      setListCategoryItems(cleanCategoryItems);
      const cleanSubcategoryItems = relatedProducts.relatedProductSubCategories
        ? relatedProducts.relatedProductSubCategories.map(subcategory => ({
            id: subcategory.id,
            name: subcategory.name,
            mainImage: null,
          }))
        : [];
      setListSubcategoriesItems(cleanSubcategoryItems);

      switch (criteria) {
        case 'PRODUCT':
          setListItems(cleanProductItems);
          break;
        case 'PRODUCT_CATEGORY':
          setListItems(cleanCategoryItems);
          break;
        case 'PRODUCT_SUB_CATEGORY':
          setListItems(cleanSubcategoryItems);
          break;
        default:
          break;
      }

      setRelatedProductsOrigin(criteria);
    }
  }, [
    hasRelatedProductFeature,
    relatedProducts,
    setEnabledRelatedProducts,
    setListCategoryItems,
    setListItems,
    setListProductsItems,
    setListSubcategoriesItems,
    setRelatedProductsOrigin,
  ]);

  useEffect(() => {
    if (product) {
      if (
        product.bulkPriceSetting &&
        product.bulkPriceSetting.bulkPrices &&
        product.bulkPriceSetting.bulkPrices.length
      ) {
        const temp = product.bulkPriceSetting.bulkPrices;

        if (temp) {
          const newLines = temp.map(el => {
            const {type, ...rest} = el;
            const draftLine = {
              ...rest,
              taxedUnitPrice: enabledTaxInclusivePricing
                ? el.unitPrice
                : calcPriceWithTax(Number(el.unitPrice)),
              unitPrice: enabledTaxInclusivePricing
                ? calcPriceWithoutTax(Number(el.unitPrice))
                : el.unitPrice,
              minQuantity: parseInt(el.minQuantity),
            };
            return draftLine;
          });

          prependBulkPrice(newLines);
        }

        return () => {
          temp.forEach((v, idx) => removeBulkPrice(idx));
        };
      }
    }
    // eslint-disable-next-line
  }, [
    product,
    // enabledTaxInclusivePricing,
    // calcPriceWithoutTax,
    // calcPriceWithTax,
    // prepend,
    // removeBulkPrice,
  ]);

  useEffect(() => {
    if (product) {
      if (
        product.bulkPriceSetting &&
        product.bulkPriceSetting.bulkPrices &&
        product.bulkPriceSetting.bulkPrices.length
      ) {
        const temp = product.bulkPriceSetting.bulkPrices;

        if (temp) {
          temp.forEach((line, idx) => {
            const prevTaxedUnitPrice = getValues(
              `bulkPriceLine.${idx}.taxedUnitPrice`
            );
            if (prevTaxedUnitPrice !== '') {
              setValue(
                `bulkPriceLine.${idx}.unitPrice`,
                calcPriceWithoutTax(Number(prevTaxedUnitPrice))
              );
            } else setValue(`bulkPriceLine.${idx}.unitPrice`, '');
          });
        }
      }
    }
  }, [product, setValue, calcPriceWithTax, getValues, calcPriceWithoutTax]);

  useEffect(() => {
    if (product) {
      if (product.bulkPriceSetting && product.bulkPriceSetting.enabled)
        setBulkPriceShow(true);
    }
  }, [product]);

  useEffect(() => {
    if (!product) return;
    if (product.productVariantSetting) {
      const {options, variants, enabled} = product.productVariantSetting;
      setVariantsShow(enabled);
      if (options) {
        setProductCriteria(
          options.map(o => {
            const {values, ...rest} = o;
            const displayType =
              rest.displayType === 'IMAGE'
                ? 'IMAGE'
                : rest.displayType === 'COLOR'
                ? 'COLOR'
                : 'TEXT';
            return {
              criterion: {
                ...rest,
                displayType,
                imageShape: rest.imageShape || 'SQUARE',
              },
              variants: values,
            };
          })
        );

        if (variants)
          setVariantsTable(
            variants.map(v => ({
              image: v.imageId || '',
              options: v.combinations,
              price: enabledTaxInclusivePricing
                ? calcPriceWithoutTax(Number(v.price))
                : v.price,
              taxedPrice:
                v.price && !isNaN(Number(v.price))
                  ? enabledTaxInclusivePricing
                    ? v.price
                    : calcPriceWithTax(Number(v.price))
                  : '',
              offerPrice: v.offerPrice
                ? enabledTaxInclusivePricing
                  ? calcPriceWithoutTax(Number(v.offerPrice))
                  : v.offerPrice
                : '',
              taxedOfferPrice:
                v.offerPrice && !isNaN(Number(v.offerPrice))
                  ? enabledTaxInclusivePricing
                    ? v.offerPrice
                    : calcPriceWithTax(Number(v.offerPrice))
                  : '',
              bulkPrice: v.enabledBulkPrice,
              stock: typeof v.stock === 'number' ? v.stock : undefined,
              sku: v.sku || '',
              enabled: v.enabled,
            }))
          );
        if (variants)
          oldVariantsTableRef.current = variants.map(v => ({
            image: v.imageId || '',
            options: v.combinations,
            price: enabledTaxInclusivePricing
              ? calcPriceWithoutTax(Number(v.price))
              : v.price,
            taxedPrice:
              v.price && !isNaN(Number(v.price))
                ? enabledTaxInclusivePricing
                  ? v.price
                  : calcPriceWithTax(Number(v.price))
                : '',
            offerPrice: v.offerPrice
              ? enabledTaxInclusivePricing
                ? calcPriceWithoutTax(Number(v.offerPrice))
                : v.offerPrice
              : '',
            taxedOfferPrice:
              v.offerPrice && !isNaN(Number(v.offerPrice))
                ? enabledTaxInclusivePricing
                  ? v.offerPrice
                  : calcPriceWithTax(Number(v.offerPrice))
                : '',
            bulkPrice: v.enabledBulkPrice,
            stock: typeof v.stock === 'number' ? v.stock : undefined,
            sku: v.sku || '',
            enabled: v.enabled,
          }));
      }
    }
  }, [
    calcPriceWithTax,
    product,
    enabledTaxInclusivePricing,
    calcPriceWithoutTax,
  ]);

  useEffect(() => {
    if (product) {
      setValue('price', calcProductBasePrice(product));

      if (
        product &&
        product.offerPriceSetting &&
        product.offerPriceSetting.enabled
      ) {
        const prevOfferPrice = product.offerPriceSetting.price;
        if (!prevOfferPrice) return;
        if (enabledTaxInclusivePricing) {
          setValue('taxedOfferPrice', prevOfferPrice);
          setValue('offerPrice', calcPriceWithoutTax(Number(prevOfferPrice)));
        } else {
          setValue('offerPrice', prevOfferPrice);
          setValue('taxedOfferPrice', calcPriceWithTax(Number(prevOfferPrice)));
        }
      }
    }
  }, [
    calcPriceWithTax,
    calcPriceWithoutTax,
    calcProductBasePrice,
    enabledTaxInclusivePricing,
    product,
    setValue,
  ]);

  useEffect(() => {
    if (product) {
      if (
        product?.bulkPriceSetting &&
        typeof product.bulkPriceSetting.enabledMixAndMatch !== 'undefined'
      ) {
        setAllowMixNMatch(!!product.bulkPriceSetting.enabledMixAndMatch);
      }
      if (
        product?.stockSetting &&
        typeof product.stockSetting.enabled !== 'undefined'
      ) {
        setInventarioShow(!!product.stockSetting.enabled);
      }
    }
  }, [history, product]);

  const hasStockByProductFeature = useIsFeatureInTier('STOCK_BY_PRODUCT');
  const businessModel = useStoreBusinessModel();

  const [inventarioShow, setInventarioShow] = useState(false);
  const handleToggleInventario = e => {
    if (!hasStockByProductFeature) {
      setTierRestrictionModal(true);
    } else setInventarioShow(e.target.checked);
  };

  const [tempSelectedValues, setTempSelectedValues] = useState<
    ProductVariantOptionValue[]
  >([]);

  const [openSelectionModal, setOpenSelectionModal] = useState<boolean>(false);

  const globalProductVariantOptions = useTypedSelector(
    state => state.products.globalProductVariantOptions
  );

  const [
    variantOptionToEdit,
    setVariantOptionToEdit,
  ] = useState<ProductVariantOption | null>(null);
  const [variantOptionIndexEdit, setVariantOptionIndexEdit] = useState(null);
  const [variantsShow, setVariantsShow] = useState(false);

  /*
    criterion: {
      id: 'adasda
      name: 'adasd,
    },
    variants: [
      { id: 'abc', name: 'asdasd }
    ]


  */
  const [productCriteria, setProductCriteria] = useState<ProductCriteria[]>([
    {
      criterion: {},
      variants: [],
    },
  ]);
  const [variantsTable, setVariantsTable] = useState<
    {
      image: string | number,
      options: {[key: string]: string},
      price: string,
      taxedPrice?: string,
      offerPrice?: string,
      taxedOfferPrice?: string,
      bulkPrice?: boolean,
      stock?: number,
      sku?: string,
      enabled?: boolean,
    }[]
  >([]);
  const oldVariantsTableRef = useRef<
    {
      image: string | number,
      options: {[key: string]: string},
      price: string,
      taxedPrice: string,
      offerPrice?: string,
      taxedOfferPrice?: string,
      bulkPrice?: boolean,
      stock?: number,
      sku?: string,
      enabled?: boolean,
    }[]
  >([]);
  const [allowMixNMatch, setAllowMixNMatch] = useState(false);
  const [bulkPriceOfferPriceAlert, setBulkPriceOfferPriceAlert] = useState(
    false
  );
  const handleAllowMixNMatch = e => {
    setBulkPriceOfferPriceAlert(false);
    if (e.target.checked) {
      for (var i = 0; i < variantsTable.length; i++) {
        if (variantsTable[i].offerPrice) {
          setBulkPriceOfferPriceAlert(true);
          return;
        }
      }
    }
    setAllowMixNMatch(e.target.checked);
  };

  const storeHasProductVariantsFeature = useIsFeatureInTier('PRODUCT_VARIANTS');

  const handleVariantsShow = e => {
    if (!storeHasProductVariantsFeature) {
      setTierRestrictionModal(true);
    } else setVariantsShow(e.target.checked);
  };

  // const handleChangeCriteria = (value: ProductVariantOption, i: number) => {
  //   var aux = [...productCriteria];
  //   aux[i].criterion = value;
  //   setProductCriteria(aux);
  //   // handleChangeVariantsTable(null, null, -1, aux);
  // };

  function generateCombinations(
    criterias,
    currentIndex = 0,
    currentCombination = []
  ) {
    if (currentIndex === criterias.length) {
      const combinationObject = currentCombination.reduce(
        (acc, {criterion, variant}) => {
          acc.options[criterion.name] = variant.name;
          return acc;
        },
        {options: {}}
      );
      return [combinationObject];
    }

    const currentCriterion = criterias[currentIndex];
    const combinations = [];

    for (const variant of currentCriterion.variants) {
      const newCombination = [
        ...currentCombination,
        {criterion: currentCriterion.criterion, variant},
      ];
      const subCombinations = generateCombinations(
        criterias,
        currentIndex + 1,
        newCombination
      );
      combinations.push(...subCombinations);
    }

    return combinations;
  }

  const handleChangeCriteriaVariants = (
    newOption: ProductVariantOption,
    newValues: ProductVariantOptionValue[],
    i: number
  ) => {
    var aux = [...productCriteria];
    aux[i].criterion = newOption;
    aux[i].variants = newValues;
    setProductCriteria(aux);
    handleUpdateVariantsCombination(aux);
  };

  const handleAddCriteria = () => {
    setProductCriteria(prev => [...prev, {criterion: {}, variants: []}]);
  };

  const handleRemoveCriteria = i => {
    if (i === 0 && productCriteria.length === 1) {
      setProductCriteria([]);
      setVariantsTable([]);
    } else {
      setProductCriteria(prev => {
        const newProductCriteria = prev
          .slice(0, i)
          .concat(prev.slice(i + 1, prev.length));
        handleUpdateVariantsCombination(newProductCriteria);
        return newProductCriteria;
      });
    }
  };

  // const handleChangeVariants = (value: ProductVariantOptionValue[], i) => {
  //   setProductCriteria(prev => {
  //     handleChangeVariantsTable(prev[i].variants, value, i);
  //     const aux = [...prev];
  //     aux[i].variants = value;
  //     return aux;
  //   });
  // };

  const handleUpdateVariantsCombination = (pProductCriteria = []) => {
    const providedProductCriteria = pProductCriteria.filter(
      p => p.variants.length
    );

    const newCombinations = generateCombinations(providedProductCriteria);

    const newVariantsTable = [];

    const nOptions = providedProductCriteria.length;
    const oldVariantsTable = oldVariantsTableRef.current;
    const oldNOptions =
      oldVariantsTable.length > 0
        ? Object.keys(oldVariantsTable[0].options).length
        : 0;

    const getCombinationKey = (opts = {}) => {
      const keys = Object.keys(opts).sort();
      return keys.map(k => `${k}=${opts[k]}`).join('|');
    };

    const price = product
      ? calcProductBasePrice(product)
      : getValues('price')
      ? getValues('price')
      : '';

    const offerPrice = getValues('offerPrice');
    const taxedOfferPrice = getValues('taxedOfferPrice');

    const emptyRow = {
      image: product && product.mainImage ? product.mainImage.id : 0,
      options: undefined,
      price,
      taxedPrice: !isNaN(Number(price))
        ? String(calcPriceWithTax(Number(price)))
        : '',
      offerPrice,
      taxedOfferPrice: taxedOfferPrice,
      bulkPrice: false,
      stock: undefined,
      sku: '',
      enabled: true,
    };

    for (let {options} of newCombinations) {
      if (nOptions === oldNOptions) {
        const oldMap = new Map(
          oldVariantsTable.map(r => [getCombinationKey(r.options), r])
        );
        const prev = oldMap.get(getCombinationKey(options));
        if (prev) {
          newVariantsTable.push({
            ...prev,
            options,
            price: prev.price || getValues('price') || '',
            offerPrice:
              prev.offerPrice ?? (getValues('offerPrice') || undefined),
          });
        } else {
          newVariantsTable.push({...emptyRow, options});
        }
      } else {
        newVariantsTable.push({...emptyRow, options});
      }
    }

    oldVariantsTableRef.current = variantsTable.map(v => ({
      ...v,
      taxedPrice: v.taxedPrice || '',
      enabled: !!v.enabled,
    }));
    setVariantsTable(
      newVariantsTable.map(v => ({
        ...v,
        taxedPrice: v.taxedPrice || '',
        enabled: !!v.enabled,
      }))
    );
  };

  const taxesApplied = product
    ? [...innerProductTaxes, ...categoryTaxes]
    : [...orderTaxes, ...categoryTaxes];

  // const handleChangeVariantsTable = (
  //   oldArray,
  //   newArray,
  //   index,
  //   providedProductCriteria
  // ) => {
  //   let arrayOfArrays = [];
  //   let optionKeys = [];
  //   let pc;
  //   if (providedProductCriteria) pc = providedProductCriteria;
  //   else pc = productCriteria;
  //   pc.forEach((c, i) => {
  //     if (i === index) {
  //       if (newArray && newArray.length > 0) {
  //         arrayOfArrays.push(newArray);
  //         optionKeys = [...optionKeys, c.criterion?.label || `${i}`];
  //       }
  //     } else {
  //       arrayOfArrays.push(c.variants);
  //       optionKeys = [...optionKeys, c.criterion?.label || `${i}`];
  //     }
  //   });
  //   // productCriteria.forEach((c, i) => {
  //   //   arrayOfArrays.push(c.variants);
  //   //   optionKeys = [...optionKeys, c.criterion || `${i}`];
  //   // });
  //   // console.log('arrayOfArrays: ', JSON.stringify(arrayOfArrays));
  //   const combinedVariants = combineArrays(arrayOfArrays, optionKeys);
  //   // const combinedVariants = combineArraysRecursively(
  //   //   arrayOfArrays,
  //   //   optionKeys
  //   // );
  //   // console.log('combinedVariants: ', combinedVariants);
  //   let newVariantsTable = [];
  //   // let startingI = 0;
  //   const nOptions = optionKeys.length;
  //   const oldNOptions =
  //     oldVariantsTable.length > 0
  //       ? Object.keys(oldVariantsTable[0].options).length
  //       : 0;

  //   const price = product
  //     ? product.price
  //     : getValues('price')
  //     ? getValues('price')
  //     : '';
  //   const offerPrice =
  //     product && product.offerPriceSetting && product.offerPriceSetting.enabled
  //       ? product.offerPriceSetting.price
  //       : getValues('offerPrice')
  //       ? getValues('offerPrice')
  //       : '';

  //   const emptyRow = {
  //     image: product && product.mainImage ? product.mainImage.id : 0,
  //     options: undefined,
  //     price,
  //     taxedPrice: !isNaN(Number(price)) ? calcPriceWithTax(Number(price)) : '',
  //     offerPrice,
  //     taxedOfferPrice: !isNaN(Number(offerPrice))
  //       ? calcPriceWithTax(Number(offerPrice))
  //       : '',
  //     bulkPrice: false,
  //     stock: undefined,
  //     sku: '',
  //     enabled: true,
  //   };
  //   combinedVariants.forEach(options => {
  //     if (nOptions === oldNOptions) {
  //       const oldRow = oldVariantsTable.filter(
  //         row => JSON.stringify(row.options) === JSON.stringify(options)
  //       );
  //       if (oldRow.length > 0) {
  //         if (!oldRow[0].offerPrice)
  //           oldRow[0].offerPrice = getValues('offerPrice')
  //             ? getValues('offerPrice')
  //             : undefined;
  //         if (!oldRow[0].price)
  //           oldRow[0].price = getValues('price') ? getValues('price') : '';
  //         newVariantsTable.push({...oldRow[0]});
  //       } else newVariantsTable.push({...emptyRow, options: options});
  //     } else {
  //       newVariantsTable.push({...emptyRow, options: options});
  //     }
  //   });
  //   setOldVariantsTable(variantsTable);
  //   setVariantsTable(newVariantsTable);
  // };

  const handleChangeTaxedVariantPrice = (
    value: string,
    i: number,
    taxed = false
  ) => {
    setVariantsTable(prev => {
      let aux = [...prev];
      if (!taxed) {
        aux[i].taxedPrice = value;
        aux[i].price = value === '' ? '' : calcPriceWithoutTax(Number(value));
      } else {
        aux[i].taxedPrice = value === '' ? '' : calcPriceWithTax(Number(value));
      }
      return aux;
    });
  };

  const handleChangeVariantPrice = (value: string, i: number) => {
    setVariantsTable(prev => {
      let aux = [...prev];

      aux[i].taxedPrice = calcPriceWithTax(Number(value));
      aux[i].price = value;
      return aux;
    });
  };

  const handleChangeVariantTaxedOfferPrice = (
    value: string,
    i: number,
    taxed = false
  ) => {
    setVariantsTable(prev => {
      let aux = [...prev];
      if (!taxed) {
        aux[i].taxedOfferPrice = value;

        aux[i].offerPrice =
          value === '' ? '' : String(calcPriceWithoutTax(Number(value)));
      } else {
        aux[i].taxedOfferPrice =
          value === '' ? '' : String(calcPriceWithTax(Number(value)));
      }
      return aux;
    });
  };

  const handleChangeVariantOfferPrice = (value: string, i: number) => {
    setVariantsTable(prev => {
      let aux = [...prev];

      aux[i].taxedOfferPrice = calcPriceWithTax(Number(value));
      aux[i].offerPrice = String(value);
      return aux;
    });
  };

  const [variantBulkPriceAlertOpen, setVariantBulkPriceAlertOpen] = useState(
    false
  );
  const rowToChangeRef = useRef(-1);
  const valueToChangeToRef = useRef(false);
  const checkBulkPriceAlertActivation = (value, index) => {
    for (var i = 0; i < variantsTable.length; i++)
      if (variantsTable[i].bulkPrice) return false;
    rowToChangeRef.current = index;
    valueToChangeToRef.current = value;
    setVariantBulkPriceAlertOpen(true);
    return true;
  };
  const handleChangeVariantBulkPrice = (value: boolean, i: number) => {
    var openModal = false;
    if (value) openModal = checkBulkPriceAlertActivation(value, i);
    if (!openModal)
      setVariantsTable(prev => {
        let aux = [...prev];
        aux[i].bulkPrice = value;
        return aux;
      });
  };
  const handleChangeVariantStock = (value: number, i: number) => {
    setVariantsTable(prev => {
      let aux = [...prev];
      aux[i].stock = value;
      return aux;
    });
  };
  const handleChangeVariantSKU = (value: string, i: number) => {
    setVariantsTable(prev => {
      let aux = [...prev];
      aux[i].sku = value;
      return aux;
    });
  };

  const handleToggleVariantsRow = i => {
    setVariantsTable(prev => {
      let aux = [...prev];
      aux[i].enabled = !prev[i].enabled;
      return aux;
    });
  };

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [variantImageModalOpen, setVariantImageModalOpen] = useState(false);
  const [variantImageEditing, setVariantImageEditing] = useState(-1);
  const [defaultVariantImage, setDefaultVariantImage] = useState('');

  const handleVariantImageModalOpen = (i: number) => {
    setVariantImageModalOpen(true);
    setVariantImageEditing(i);
    setDefaultVariantImage(variantsTable[i].image);
  };

  const handleVariantImageModalClose = useCallback(() => {
    setVariantImageModalOpen(false);
    setVariantImageEditing(-1);
    setDefaultVariantImage('');
  }, []);

  const handleSetVariantImage = useCallback(
    (imageId: string | number) => {
      if (variantImageEditing >= 0)
        setVariantsTable(prev => {
          let aux = [...prev];
          aux[variantImageEditing].image = imageId;
          return aux;
        });
    },
    [variantImageEditing]
  );
  const [variantSkuErrors, setVariantSkuErrors] = useState({});
  const [variantSkuErrorsSnack, setVariantSkuErrorsSnack] = useState(false);
  const validateVariantSku = (value: string, index: number) => {
    const lookup = variantsTable.reduce((a, e) => {
      if (e.sku) {
        a[String(e.sku)] = ++a[e.sku] || 0;
      }
      return a;
    }, {});
    // console.log(lookup);
    const result = variantsTable.reduce((r, row, i) => {
      if (row.sku) {
        if (!/^[0-9A-Z_-]+$/.test(row.sku))
          r[`variant-sku-${i}`] =
            'El SKU solo puede contener mayúsculas, números y guiones';
        else if (lookup[String(row.sku)])
          r[`variant-sku-${i}`] = 'SKU repetido';
      }
      return r;
    }, {});
    // console.log(result);
    setVariantSkuErrors(result);
  };

  const isValidVariantsStock = useMemo(() => {
    if (!variantsTable) return;
    const stockRegex = /^\d+(\.\d{1,2})?$/;
    return (
      variantsTable.filter(vr => !stockRegex.test(String(vr.stock))).length < 1
    );
  }, [variantsTable]);

  useEffect(() => {
    if (!variantsShow || !inventarioShow) {
      if (variantsTable && variantsTable.length)
        variantsTable.forEach((vr, idx) => clearErrors(`variant-stock-${idx}`));
    } else {
      if (variantsTable && variantsTable.length && isSubmitted) {
        const stockRegex = /^\d+(\.\d{1,2})?$/;
        variantsTable.forEach((vr, idx) => {
          // console.log(vr.stock);
          if (stockRegex.test(String(vr.stock))) {
            clearErrors(`variant-stock-${idx}`);
          } else
            setError(`variant-stock-${idx}`, {
              type: 'manual',
              message: 'Stock inválido',
            });
        });
      }
    }
  }, [
    clearErrors,
    inventarioShow,
    isSubmitted,
    setError,
    variantsShow,
    variantsTable,
  ]);

  const buildProductVariantsRequest = () => {
    var request: ProductVariantsRequest = {options: [], variants: []};
    request.options = productCriteria.map(c => ({
      id: c.criterion.id,
      values: c.variants.filter(v => v?.id != null).map(v => String(v.id)),
    }));

    request.variants = variantsTable.map(row => {
      const price = enabledTaxInclusivePricing ? row.taxedPrice : row.price;
      const offerPrice = enabledTaxInclusivePricing
        ? row.taxedOfferPrice
        : row.offerPrice;
      return {
        imageId: row.image !== '' ? row.image : undefined,
        // options: row.options,
        combination: row.options,
        price: price ? price.toString() : '0',
        offerPrice: offerPrice ? offerPrice.toString() : undefined,
        enabledBulkPrice: !!row.bulkPrice,
        stock: typeof row.stock === 'number' ? row.stock : undefined,
        sku: row.sku || undefined,
        enabled: !!row.enabled,
      };
    });
    // console.log(request);
    return request;
  };

  const [bulkErrorMessage, setBulkErrorMessage] = useState('');

  const isDirtyForm = useMemo(() => {
    //This is due to useFieldArray doesnt provide a way to setDefautlValue or shouldDirty
    //to its childrens on append fn.
    // https://github.com/react-hook-form/react-hook-form/issues/3525
    const {...otherDirtyFields} = dirtyFields;
    return otherDirtyFields && Object.keys(otherDirtyFields).length > 0;
    //eslint-disable-next-line
  }, [formState]);

  const buildRelatedProductsRequest = () => {
    let idsArray = [];
    let rq: {[key: string]: any} = {enabled: true};
    if (relatedProductsOrigin === 'PRODUCT') {
      idsArray = listProductsItems.map(p => p.id);
      rq.criteria = 'PRODUCT';
      rq.products = idsArray;
    } else if (relatedProductsOrigin === 'PRODUCT_CATEGORY') {
      idsArray = listCategoryItems.map(p => p.id);
      rq.criteria = 'PRODUCT_CATEGORY';
      rq.productCategories = idsArray;
    } else {
      idsArray = listSubcategoriesItems.map(p => p.id);
      rq.criteria = 'PRODUCT_SUB_CATEGORY';
      rq.productSubCategories = idsArray;
    }
    return rq;
  };

  const buildProductSectionRequest = async (productSections = []) => {
    const filteredSections = productSections.filter(el => el.title);

    const sections = [];

    for (let i = 0; i < filteredSections.length; i++) {
      const section = filteredSections[i];
      const productSectionImageLayout =
        sectionImageLayoutType[i] ||
        sectionImages[i]?.productSectionImageLayout ||
        undefined;

      const sectionImage = sectionImages[i];

      let file = sectionImage?.file || undefined;
      if (isDuplicating && sectionImage.imageId) {
        file = await createFile(getImageUrl(sectionImage.imageId)).then(
          res => res.file
        );
      }

      const sectionRequest = {
        ...section,
        enabled: true,
        content: section.content || undefined,
        productSectionImageLayout: productSectionImageLayout || undefined,
        imageId: sectionImage?.imageId || undefined,
        file,
      };

      sections.push(sectionRequest);
    }

    return sections;
  };

  // useEffect(() => {
  //   if (totalProductTaxesFactor) {
  //     const prevProductPrice = getValues('price');
  //     if (prevProductPrice && !isNaN(Number(prevProductPrice)))
  //       setValue('taxedPrice', calcPriceWithTax(prevProductPrice));
  //     const prevOfferPrice = getValues('offerPrice');

  //     const numberOfferPrice = Number(prevOfferPrice);
  //     if (prevOfferPrice !== '' && !isNaN(numberOfferPrice)) {
  //       setValue('taxedOfferPrice', calcPriceWithTax(prevOfferPrice));
  //     }
  //   }
  // }, [
  //   calcPriceWithTax,
  //   calcPriceWithoutTax,
  //   getValues,
  //   setValue,
  //   totalProductTaxesFactor,
  // ]);

  // useEffect(() => {
  //   if (bulkPriceLines) {
  //     const updatedLines = bulkPriceLines.map(el => {
  //       const temp = {...el};
  //       if (!isNaN(Number(temp.unitPrice)))
  //         temp.taxedUnitPrice = calcPriceWithTax(Number(temp.unitPrice));
  //       return temp;
  //     });
  //     setBulkPriceLines(updatedLines);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [calcPriceWithTax]);

  // useEffect(() => {
  //   if (variantsTable) {
  //     const updatedLines = variantsTable.map(el => {
  //       const temp = {...el};
  //       if (!isNaN(Number(temp.price)))
  //         temp.taxedPrice = calcPriceWithTax(Number(temp.price));
  //       if (temp.offerPrice !== '' && !isNaN(Number(temp.offerPrice))) {
  //         temp.taxedOfferPrice = calcPriceWithTax(Number(temp.offerPrice));
  //       }
  //       return temp;
  //     });
  //     setVariantsTable(updatedLines);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [calcPriceWithTax]);

  // const getFormErrors = () => {
  //   trigger().then(() => {
  //     const draftErrors = getFormErrorsProgramatically(errors);

  //     console.log(draftErrors);
  //     // if (draftErrors.length) {
  //     //   const incorrectFields = draftErrors.map(f => f[1]).join(', ');

  //     //   showAlert(
  //     //     `Tienes algunos campos incorrectos: \n
  //     //     ${incorrectFields}
  //     //     `,
  //     //     {
  //     //       type: 'warning',
  //     //       duration: 6000,
  //     //     }
  //     //   );
  //     // }
  //   });
  // };

  const isValidRequestForVariants = () => {
    let error;

    if (variantsShow && variantsTable.length < 1)
      error =
        'Si tienes activadas las variantes de producto, tienes que crear al menos una variante.';

    for (const variant of variantsTable) {
      if (!variant.price || !variant.taxedPrice) {
        error = 'Debes asignarle un precio a tus variantes';
      }

      if (variant.offerPrice && variant.price) {
        if (
          parseInt(variant.offerPrice) === 0 ||
          parseInt(variant.taxedOfferPrice) === 0
        )
          error =
            'Si tienes precio de oferta en tus variantes, este debe ser mayor a 0';
        else if (parseInt(variant.offerPrice) > parseInt(variant.price))
          error =
            'El precio de oferta debe ser menor que el precio de tus variantes';
      }

      if (error) break;
    }

    if (error)
      showAlert(error, {
        type: 'info',
        duration: 4000,
        clearPreviousAlerts: true,
      });

    return !error;
  };

  const isValidRequestForBulkPrice = (bulkPrices: any[] = []) => {
    let error;

    if (bulkPriceShow && bulkPrices.length < 1)
      error =
        'Si tienes activado los precios al por mayor, tienes que crear al menos uno.';

    if (error)
      showAlert(error, {
        type: 'info',
        duration: 4000,
        clearPreviousAlerts: true,
      });

    return !error;
  };

  const isValidRequestForRelatedProducts = () => {
    let error;

    let idsArray = [];
    if (relatedProductsOrigin === 'PRODUCT') {
      idsArray = listProductsItems.map(p => p.id);
    } else if (relatedProductsOrigin === 'PRODUCT_CATEGORY') {
      idsArray = listCategoryItems.map(p => p.id);
    } else {
      idsArray = listSubcategoriesItems.map(p => p.id);
    }

    if (enabledRelatedProducts && idsArray.length < 1)
      error =
        'Si tienes activado los productos relacionados, tienes que agregar al menos uno.';

    if (error)
      showAlert(error, {
        type: 'info',
        duration: 4000,
        clearPreviousAlerts: true,
      });

    return !error;
  };

  const isValidRequestForSizeGuide = () => {
    let error;

    if (
      enabledSizeGuide &&
      !sizeGuideSelected &&
      sizeCharts &&
      sizeCharts.length > 0
    )
      error =
        'Si tienes activado la guia de tallas, tienes que agregar al menos una.';

    if (error)
      showAlert(error, {
        type: 'info',
        duration: 4000,
        clearPreviousAlerts: true,
      });

    return !error;
  };

  const handleEditSubmit = async data => {
    // validateBulk(bulkPriceLines, clearErrors, setError);
    setIsSubmitted(true);

    const isValidVariantRequest = isValidRequestForVariants();

    const isValidBulkPriceRequest = isValidRequestForBulkPrice(
      data.bulkPriceLine
    );

    const isValidRelatedProductsRequest = isValidRequestForRelatedProducts();
    const isValidSizeGuideRequest = isValidRequestForSizeGuide();

    if (!isValidVariantRequest) return;
    if (!isValidBulkPriceRequest) return;
    if (!isValidRelatedProductsRequest) return;
    if (!isValidSizeGuideRequest) return;

    const validImages = savedImages.length + cropResult.length > 0;

    // trigger().then(s => {
    //   if (!s) return;
    if (!validImages) {
      setMissingImage(true);
      showAlert('Debes colocar una imagen a tu producto', {type: 'info'});
      scrollTop('smooth');
    } else if (Object.values(variantSkuErrors).length > 0) {
      setVariantSkuErrorsSnack(true);
      scrollTop('smooth', undefined, 9999);
    } else if (inventarioShow && variantsShow && !isValidVariantsStock) {
      scrollTop('smooth', undefined, 9999);
    } else {
      const bulkPrices = data.bulkPriceLine;

      const productFeatures =
        data.productFeatures &&
        data.productFeatures
          .filter(item => !!item.name && !!item.description)
          .map(item => {
            return {
              name: item.name,
              description: item.description,
            };
          });

      const price = enabledTaxInclusivePricing ? data.taxedPrice : data.price;
      const offerPrice = enabledTaxInclusivePricing
        ? data.taxedOfferPrice
        : data.offerPrice;

      const videos = productVideosRef.current;
      if (product && !isDuplicating) {
        const productOptionSetting = productOptionSettingRef.current;
        let productOptionSettingRequest: ProductOptionRequest | null = null;

        if (productOptionSetting && hasProductOptionsFeature) {
          productOptionSettingRequest = mapProductOptionSettingToRequest(
            productOptionSetting
          );
        }

        const productRequest: ProductRequest = {
          id: product.id,
          name: product.name === data.name ? undefined : data.name,
          videos: videos.map(v => ({
            url: v.url,
            socialMediaType: v.socialMediaType,
          })),
          sku: data.sku !== product.sku ? data.sku : undefined,
          description:
            product.description === productDescription
              ? undefined
              : productDescription,
          price: product.price === price ? undefined : price,
          offerPriceSetting: offerPrice
            ? product.offerPriceSetting &&
              product.offerPriceSetting.enabled &&
              product.offerPriceSetting.price === offerPrice
              ? undefined
              : Number(offerPrice) > 0
              ? {enabled: true, offerPrice: String(offerPrice)}
              : undefined
            : {
                enabled: false,
              },
          stockSetting: inventarioShow
            ? product.stockSetting &&
              product.stockSetting.enabled &&
              !isNaN(parseInt(product.stockSetting.stock)) &&
              parseInt(product.stockSetting.stock) === parseInt(data.stock)
              ? undefined
              : {
                  enabled: inventarioShow,
                  stock: isNaN(parseInt(data.stock))
                    ? undefined
                    : parseInt(data.stock),
                }
            : product.stockSetting &&
              !product.stockSetting.enabled &&
              parseInt(product.stockSetting.stock) === parseInt(data.stock)
            ? undefined
            : {
                enabled: false,
              },
          seoSetting: enabledSeo
            ? {
                enabled: true,
                title: data.metaTitle,
                description: data.metaDescription,
              }
            : hasProductMetadataFeature
            ? productMetadata
              ? {enabled: false}
              : undefined
            : undefined,
          sizeChartSetting: enabledSizeGuide
            ? {enabled: true, sizeChartId: sizeGuideSelected}
            : hasProductSizeChartFeature
            ? productSizeChart
              ? {enabled: false}
              : undefined
            : undefined,
          // cost: data.cost || undefined,
          imageFiles: cropResult.map(result => result.file),
          images: savedImages,
          bulkPriceSetting: {
            enabled: Boolean(bulkPriceShow),
            enabledMixAndMatch:
              bulkPriceShow &&
              variantsTable &&
              variantsTable.filter(row => row.bulkPrice).length > 1 &&
              allowMixNMatch
                ? allowMixNMatch
                : false,
            bulkPriceLines: bulkPriceShow
              ? bulkPrices.map(el => {
                  const {taxedUnitPrice, minQuantity, ...bp} = el;
                  bp.minQuantity = parseInt(minQuantity);
                  if (enabledTaxInclusivePricing) bp.unitPrice = taxedUnitPrice;
                  return bp;
                })
              : undefined,
          },
          metadataSetting: {},
          productOptionsSetting: productOptionSettingRequest || undefined,
        };

        if (priceDisplayMode === 'SHOW_LABEL') {
          productRequest.metadataSetting = productRequest.metadataSetting || {};
          productRequest.metadataSetting.priceDisplay = {
            mode: 'LABEL',
            label: data.ctaLabel || '',
            url: data.ctaUrl || '',
          };
        } else {
          productRequest.metadataSetting = productRequest.metadataSetting || {};
          productRequest.metadataSetting.priceDisplay = {
            mode: 'PRICE',
          };
        }

        if (hasProductFeaturesFeature) {
          const specifications = productFeatures ?? [];
          const specificationSetting = {
            enabled: specifications.length > 0,
            specifications,
          };
          productRequest.specificationSetting = specificationSetting;
        }

        // productRequest.sku = data.sku ? data.sku : undefined;
        const {
          categories,
          subCategories,
          subCategoryItems,
        } = categoryValuesRef.current;
        productRequest.categories = categories.map(c => c.id);
        productRequest.subCategories = subCategories.map(c => c.id);
        if (SUBCATEGORY_ITEM_ENABLED) {
          productRequest.subCategoryItems = subCategoryItems.map(c => c.id);
        }
        if (enabledRelatedProducts) {
          productRequest.relatedProductSetting = buildRelatedProductsRequest();
        } else if (hasRelatedProductFeature && relatedProducts)
          productRequest.relatedProductSetting = {enabled: false};

        if (data.productSections?.length) {
          productRequest.productSectionRequest = await buildProductSectionRequest(
            data.productSections
          );
        }

        productRequest.productVariantSetting = {};
        if (variantsShow) {
          if (!product?.productVariantSetting?.enabled)
            productRequest.productVariantSetting.enabled = true;
          if (
            JSON.stringify(oldVariantsTableRef.current) !==
            JSON.stringify(variantsTable)
          ) {
            const {options, variants} = buildProductVariantsRequest();
            productRequest.productVariantSetting = {
              ...productRequest.productVariantSetting,
              options,
              variants,
            };
          }
        } else productRequest.productVariantSetting.enabled = false;

        const isValid = isRequestValidForBulkPrices(productRequest);

        if (!isValid) {
          setBulkErrorMessage(
            'Si tienes precios al por mayor debes asignarlo a tus variantes activas.'
          );
        } else {
          editProduct({
            productRequest: productRequest,
            // productSectionsRequest,
          });
        }
      } else {
        const imageFiles = [];

        if (isDuplicating && savedImages.length > 0) {
          for (const image of savedImages) {
            const fileUrl = await getOriginalFileUrl(image.id);
            if (!fileUrl) continue;
            const result = await createFile(fileUrl).then(({file}) => file);
            imageFiles.push(result);
          }
        }

        imageFiles.push(...cropResult.map(result => result.file));

        const productOptionSetting = productOptionSettingRef.current;
        let productOptionSettingRequest: ProductOptionRequest | null = null;

        if (productOptionSetting && hasProductOptionsFeature) {
          productOptionSettingRequest = mapProductOptionSettingToRequest(
            productOptionSetting
          );
        }

        const productRequest: ProductRequest = getFilledValues(
          {
            name: data.name,
            featured:
              !!queryObject.fromOnboarding &&
              (!featuredProducts || featuredProducts.length < 6),
            videos:
              videos.length > 0
                ? videos.map(v => ({
                    url: v.url,
                    socialMediaType: v.socialMediaType,
                  }))
                : undefined,
            sku: data.sku !== '' ? data.sku : undefined,
            description: productDescription,
            price: String(price),
            offerPriceSetting: !isNaN(parseInt(offerPrice))
              ? Number(offerPrice) > 0
                ? {enabled: true, offerPrice: String(offerPrice)}
                : undefined
              : undefined,
            stockSetting: inventarioShow
              ? {
                  enabled: inventarioShow,
                  stock: isNaN(parseInt(data.stock))
                    ? undefined
                    : parseInt(data.stock),
                }
              : undefined,
            seoSetting: enabledSeo
              ? {
                  enabled: true,
                  title: data.metaTitle,
                  description: data.metaDescription,
                }
              : undefined,
            specificationSetting: {
              enabled: !!productFeatures,
              specifications: productFeatures ?? [],
            },
            sizeChartSetting: enabledSizeGuide
              ? {enabled: true, sizeChartId: sizeGuideSelected}
              : undefined,
            // cost: data.cost,
            imageFiles,
            bulkPriceSetting: {
              enabled: Boolean(bulkPriceShow),
              enabledMixAndMatch: allowMixNMatch ? allowMixNMatch : false,
              bulkPriceLines: bulkPriceShow
                ? bulkPrices.map(el => {
                    const {taxedUnitPrice, minQuantity, ...bp} = el;
                    bp.minQuantity = parseInt(minQuantity);
                    if (enabledTaxInclusivePricing)
                      bp.unitPrice = taxedUnitPrice;
                    return bp;
                  })
                : undefined,
            },
            productOptionsSetting: productOptionSettingRequest || undefined,
          },
          ['description', 'videos']
        );

        let productSectionsRequest = undefined;

        if (data.productSections?.length) {
          productSectionsRequest = await buildProductSectionRequest(
            data.productSections
          );
        }

        if (priceDisplayMode === 'SHOW_LABEL') {
          productRequest.metadataSetting = productRequest.metadataSetting || {};
          if (priceDisplayMode === 'SHOW_LABEL') {
            productRequest.metadataSetting.priceDisplay = {
              mode: 'LABEL',
              label: data.ctaLabel || '',
              url: data.ctaUrl || '',
            };
            if (!price) {
              productRequest.price = '0';
            }
          } else {
            productRequest.metadataSetting.priceDisplay = {
              mode: 'PRICE',
            };
          }
        }

        const {
          categories,
          subCategories,
          subCategoryItems,
        } = categoryValuesRef.current;
        if (categories.length) {
          productRequest.categories = categories.map(c => c.id);
        }
        productRequest.subCategories = subCategories.map(c => c.id);
        if (SUBCATEGORY_ITEM_ENABLED) {
          productRequest.subCategoryItems = subCategoryItems.map(c => c.id);
        }
        if (enabledRelatedProducts) {
          productRequest.relatedProductSetting = buildRelatedProductsRequest();
        }

        let productVariantsRequest = undefined;
        if (variantsShow) {
          productRequest.enabledProductVariants = true;
          productVariantsRequest = buildProductVariantsRequest();
        } else {
          productRequest.enabledProductVariants = false;
        }
        const isValid = isRequestValidForBulkPrices(
          productRequest,
          productVariantsRequest
        );
        if (!isValid) {
          setBulkErrorMessage(
            'Si tienes precios al por mayor debes asignarlo a tus variantes activas.'
          );
          return;
        } else {
          console.log({
            productRequest: productRequest,
            productVariantsRequest,
            productSectionsRequest,
          });
          createProduct({
            productRequest: productRequest,
            productVariantsRequest,
            productSectionsRequest,
          });
        }
      }
    }
    // });
  };
  const isRequestValidForBulkPrices = (
    productRequest: ProductRequest,
    productVariants?: ProductVariantsRequest
  ): boolean => {
    if (bulkPriceShow && variantsShow) {
      if (productRequest.productVariants)
        return Boolean(
          productRequest.productVariants.variants.some(i => i.enabledBulkPrice)
        );
      else if (
        productRequest &&
        productRequest.productVariantSetting &&
        productRequest.productVariantSetting.variants?.length
      )
        return productRequest.productVariantSetting.variants.some(
          i => !!i.enabledBulkPrice
        );
      else if (
        product &&
        product.productVariantSetting &&
        product.productVariantSetting.variants?.length
      )
        return product.productVariantSetting.variants.some(
          i => i.enabledBulkPrice
        );
      else if (productVariants) {
        return productVariants.variants.some(i => i.enabledBulkPrice);
      } else return true;
    } else return true;
  };

  const handleChangeCategories = useCallback(
    (categories, subCategories, subCategoryItems) => {
      categoryValuesRef.current = {
        categories,
        subCategories,
        subCategoryItems,
      };

      setSelectedCategories(categories);
    },
    []
  );

  useEffect(() => {
    if (
      savedCallback &&
      (deleteProductSuccess ||
        createProductSuccess ||
        editProductSuccess ||
        createFinalSettingProductSuccess)
    ) {
      if (createProductSuccess)
        analytics.fireCustomEvent('Product Created', undefined, [
          'user-guiding',
        ]);
      savedCallback();
    }
  }, [
    createFinalSettingProductSuccess,
    createProductSuccess,
    deleteProductSuccess,
    editProductSuccess,
    savedCallback,
    store,
  ]);

  useEffect(() => {
    if (storeHasProductVariantsFeature && variantsShow && !product) {
      // setValue('price', 0);
      if (variantsTable && variantsTable.length) {
        let price = parseFloat(variantsTable[0].price);
        let taxedPrice = parseFloat(variantsTable[0].taxedPrice);
        let offerPrice = parseFloat(variantsTable[0].offerPrice);
        let taxedOfferPrice = parseFloat(variantsTable[0].taxedOfferPrice);
        variantsTable.forEach(v => {
          if (parseFloat(v.price) < parseFloat(price)) price = v.price;
          if (v.offerPrice && parseFloat(v.offerPrice) < parseFloat(offerPrice))
            offerPrice = v.offerPrice;
        });

        if (!isNaN(price)) setValue('price', price);

        if (taxedPrice && !isNaN(taxedPrice))
          setValue('taxedPrice', taxedPrice);

        if (offerPrice && !isNaN(offerPrice))
          setValue('offerPrice', offerPrice);
        else setValue('offerPrice', '');

        if (taxedOfferPrice && !isNaN(taxedOfferPrice))
          setValue('taxedOfferPrice', taxedOfferPrice);
        else setValue('taxedOfferPrice', '');
      }
    }
  }, [
    storeHasProductVariantsFeature,
    product,
    setValue,
    variantsShow,
    variantsTable,
  ]);

  const handleVideosChange = newVideos => {
    productVideosRef.current = newVideos;
  };

  const [tierRestrictionModal, setTierRestrictionModal] = useState(false);

  const hasProductVideoUrlFeature = useIsFeatureInTier('PRODUCT_VIDEO_URL');

  const [premiumFeatures, setPremiumFeatures] = useState<
    {
      name: string,
      description: string,
      link?: string,
      open: boolean,
      hasFeature: boolean,
    }[]
  >([
    {
      name: 'Variantes de producto',
      description:
        'Utiliza las variantes cuando tu producto tenga tallas, colores u otros variaciones.',
      link: '',
      open: false,
      hasFeature: storeHasProductVariantsFeature,
    },
    {
      name: 'Inventarios',
      description:
        'Registra tus stocks y no pierdas control de tu inventario de productos.',
      link: '',
      open: false,
      hasFeature: hasStockByProductFeature,
    },
    {
      name: 'Precios al por mayor',
      description:
        '¿Quieres impulsar las ventas al por mayor? Tú puedes configurarlo como quieras.\nA partir de 2,3,4... unidades tu cliente obtiene un mejor precio unitario por llevar varias unidades.',
      link: '',
      open: false,
      hasFeature: hasBulkPriceFeature,
    },
    {
      name: 'Videos de producto',
      description:
        '¿Una foto no es suficiente? Puedes colocar links de youtube para mostrar tu producto y enseñar sus beneficios.',
      link: '',
      open: false,
      hasFeature: hasProductVideoUrlFeature,
    },
    {
      name: 'Múltiples fotos por producto',
      description:
        'Sube varias fotos por producto y muestra los diferentes ángulos.',
      link: '',
      open: false,
      hasFeature: hasVariousProductImagesFeature,
    },
  ]);

  const setNewImageAsMainImage = (index: number) => {
    setCropResult(prev => {
      const auxList = prev.filter((_, i) => i !== index);
      const newMainImage = prev[index];
      return [newMainImage, ...auxList];
    });
  };
  const setSavedImageAsMainImage = (imageId: string) => {
    setSavedImages(prev => {
      const auxList = prev.filter(i => i.id !== imageId);
      const newMainImage = prev.find(i => i.id === imageId);
      if (!newMainImage) return prev;
      return [newMainImage, ...auxList];
    });
  };

  const handleRemoveSavedImage = (imageId: string) => {
    setSavedImages(prev => {
      return prev.filter(i => i.id !== imageId);
    });
  };

  const handleRemoveNewImage = (index: number) => {
    setCropResult(prev => {
      return prev.filter((_, i) => i !== index);
    });
  };

  const imageSection = (
    <>
      <div className={classes.productImageContainer}>
        <div className={classes.dragHandle}></div>
        <label
          className={`${classes.imageBox} ${classes.addImageBox} ${
            isSaving || uploadDisabled ? classes.imageBoxDisabled : ''
          }`}
        >
          <input
            hidden
            type="file"
            accept="image/*"
            multiple={hasMultipleImagesFeature}
            onChange={handleFileInputChange}
          />

          <Publish color="primary" />

          <span>{`${SIZE_RECOMMENDATION[productImageShape].width} x ${SIZE_RECOMMENDATION[productImageShape].height}`}</span>
        </label>
      </div>
      <SortableList
        className={classes.imagesContainer}
        lockAxis="x"
        draggedItemClassName="dragged"
        onSortEnd={(oldIndex: number, newIndex: number) => {
          const sortedSections = arrayMove(savedImages, oldIndex, newIndex);

          setSavedImages(sortedSections);
        }}
        allowDrag={hasSortProducImagesFeature}
      >
        {savedImages.map((image, index) => (
          <SortableItem key={image.id}>
            <div className={classes.productImageContainer}>
              {hasSortProducImagesFeature ? (
                <SortableKnob>
                  <div className={classes.dragHandle}>
                    <DragHandle />
                  </div>
                </SortableKnob>
              ) : (
                <div className={classes.dragHandle}></div>
              )}
              <ButtonBase
                className={classNames(classes.imageBox, classes.editImage, {
                  [classes.mainProductImage]: index === 0,
                  [classes.secondaryImages]: index !== 0,
                })}
                onClick={() => {
                  setSavedImageAsMainImage(image.id);
                }}
              >
                <img
                  className={`${classes.image} ${
                    uploadDisabled && index !== 0
                      ? classes.imageBoxDisabled
                      : ''
                  }`}
                  src={getImageUrl(image.id)}
                  alt="no artifact that depicts visual perception for you"
                />
              </ButtonBase>
              <IconButton
                aria-label={'cancel'}
                className={classes.removeImageIcon}
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveSavedImage(image.id);
                }}
                disabled={isSaving}
              >
                <Close fontSize="small" />
              </IconButton>
            </div>
          </SortableItem>
        ))}
      </SortableList>
      <div className={classes.imagesContainer}>
        {cropResult.map(({file, preview}, index) => (
          <div className={classes.productImageContainer} key={index}>
            <div className={classes.dragHandle}></div>
            <ButtonBase
              className={`${classes.imageBox} ${classes.secondaryImages}`}
              onClick={() => {
                setNewImageAsMainImage(index);
              }}
            >
              <img
                className={classes.image}
                src={preview}
                alt="no artifact that depicts visual perception for you"
              />
              <IconButton
                aria-label={'cancel'}
                className={classes.removeImageIcon}
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveNewImage(index);
                }}
                disabled={isSaving}
              >
                <Close fontSize="small" />
              </IconButton>

              <IconButton
                aria-label={'crop'}
                className={`${
                  index === 0
                    ? classes.updateCoverImageCrop
                    : classes.updateImageCrop
                }`}
                onClick={e => {
                  e.stopPropagation();
                  imageIndexToCropRef.current = index;
                  setImagesToCrop([{file, preview}]);
                }}
                disabled={isSaving}
              >
                <Crop fontSize="small" color="primary" />
              </IconButton>
            </ButtonBase>
          </div>
        ))}
      </div>
    </>
  );

  const buildMainContent = () => {
    return (
      <>
        <div className={classes.contentSection}>
          <Typography className={classes.sectionTitle}>
            Foto de tu {businessModel === 'PRODUCT' ? 'producto' : 'servicio'}:
          </Typography>
          <div className={classes.imagesContainer}>{imageSection}</div>
          <Typography color="error" hidden={!missingImage}>
            Debe colocar una imagen.
          </Typography>
          {uploadDisabled && (
            <div className={classes.upsellRow}>
              <div className={classes.upsellMessage}>
                Mejora tu plan y carga más fotos. Muestra distintos ángulos del
                producto
              </div>
              <PremiumFeatureBadge />
            </div>
          )}
          {hasProductVideoUrlFeature && (
            <ProductVideoSection
              defaultVideos={product?.videos ?? []}
              onChangeVideos={handleVideosChange}
              disabled={isSaving}
            />
          )}
          <Typography className={classes.sectionTitle}>
            Información de tu{' '}
            {businessModel === 'PRODUCT' ? 'producto' : 'servicio'}:
          </Typography>
          <div className={classes.desktopColumns}>
            <CustomTextField
              id="product-name"
              label="Nombre del producto"
              name="name"
              autoComplete="product-name"
              error={!!errors.name}
              helperText={errors.name ? errors.name.message : ''}
              defaultValue={
                product
                  ? `${product.name} ${isDuplicating ? '(Copia)' : ''}`
                  : ''
              }
              type="text"
              inputRef={register({
                required: 'Campo obligatorio',
                pattern: {
                  value: /^\S.*$/,
                  message: 'Formato incorrecto',
                },
              })}
              disabled={isSaving}
              className={classes.productInputField}
            />
            <CustomTextField
              id="product-sku"
              label="SKU (Opcional)"
              name="sku"
              autoComplete="product-sku"
              error={!!errors.sku}
              helperText={errors.sku ? errors.sku.message : ''}
              defaultValue={product && !isDuplicating ? product.sku : ''}
              type="text"
              inputRef={register({
                pattern: {
                  value: /^[0-9A-Z_-]+$/,
                  message:
                    'El SKU solo puede contener mayúsculas, números y guiones',
                },
              })}
              disabled={isSaving}
              className={classes.productInputField}
            />
          </div>
        </div>

        <div className={classes.contentSection}>
          <Typography className={classes.sectionTitle}>Precio:</Typography>

          <RadioGroup
            name="priceDisplayMode"
            value={priceDisplayMode}
            onChange={e => {
              // setValue('priceDisplayMode', e.target.value);
              setPriceDisplayMode(e.target.value);
            }}
            row
          >
            <FormControlLabel
              value="SHOW_PRICE"
              control={<Radio color="primary" />}
              label="Mostrar precio"
            />
            <FormControlLabel
              value="SHOW_LABEL"
              control={<Radio color="primary" />}
              label={
                <div className={classes.showLabelContainer}>
                  <span>Mostrar texto</span>
                  <TiendadaTooltip placement="top" title="Oculta el precio">
                    <InfoOutlined color="primary" className={classes.infoBtn} />
                  </TiendadaTooltip>
                </div>
              }
            />
          </RadioGroup>

          {priceDisplayMode === 'SHOW_LABEL' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  className={`${classes.sectionDescription} ${classes.categories}`}
                >
                  Al seleccionar <strong>Mostrar texto</strong>, el precio se
                  ocultará y este producto no podrá añadirse al carrito ni
                  incluirse en órdenes manuales
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <CustomTextField
                  label="Texto del botón"
                  name="ctaLabel"
                  error={!!errors.ctaLabel}
                  helperText={errors.ctaLabel ? errors.ctaLabel.message : ''}
                  defaultValue={
                    product?.metadataSetting?.priceDisplay?.label || ''
                  }
                  type="text"
                  inputRef={register()}
                  disabled={isSaving}
                  className={classes.productInputField}
                />
              </Grid>
              <Grid item xs={6}>
                <CustomTextField
                  label="Enlace del botón"
                  name="ctaUrl"
                  error={!!errors.ctaUrl}
                  helperText={errors.ctaUrl ? errors.ctaUrl.message : ''}
                  defaultValue={
                    product?.metadataSetting?.priceDisplay?.url || ''
                  }
                  type="text"
                  inputRef={register()}
                  disabled={isSaving}
                  className={classes.productInputField}
                />
              </Grid>
            </Grid>
          )}

          {priceDisplayMode === 'SHOW_PRICE' && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="price"
                  defaultValue={product ? calcProductBasePrice(product) : ''}
                  control={control}
                  rules={{
                    required: 'Campo obligatorio',
                    pattern: {
                      value: /^\d+(\.\d{1,5})?$/,
                      message: 'Ingrese un monto válido',
                    },
                  }}
                  render={({onChange, value}) => (
                    <CustomTextField
                      id="product-price"
                      label="Valor de venta"
                      autoComplete="product-price"
                      error={!!errors.price}
                      helperText={errors.price ? errors.price.message : ''}
                      type="text"
                      disabled={
                        isSaving ||
                        (storeHasProductVariantsFeature && variantsShow)
                      }
                      value={value}
                      onChange={e => {
                        const value = e.target.value;
                        if (!isNaN(Number(value)))
                          setValue('taxedPrice', calcPriceWithTax(value));
                        onChange(e);
                      }}
                      className={classes.productInputField}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="taxedPrice"
                  defaultValue={product ? product.price : ''}
                  // defaultValue={productDefaultTaxedPrice}
                  control={control}
                  rules={{
                    required: 'Campo obligatorio',
                    pattern: {
                      value: /^\d+(\.\d{1,5})?$/,
                      message: 'Ingrese un monto válido',
                    },
                  }}
                  render={({onChange, value}) => (
                    <CustomTextField
                      id="taxed-product-price"
                      label="Precio de venta"
                      autoComplete="product-price"
                      error={!!errors.taxedPrice}
                      helperText={
                        errors.taxedPrice ? errors.taxedPrice.message : ''
                      }
                      type="text"
                      disabled={
                        !hasTaxesFeature ||
                        isSaving ||
                        (storeHasProductVariantsFeature && variantsShow)
                      }
                      value={value}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === '') setValue('price', '');
                        else if (!isNaN(Number(value)))
                          setValue('price', calcPriceWithoutTax(value));
                        onChange(e);
                      }}
                      className={classes.productInputField}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                  <ResumeTaxModal
                    taxes={taxesApplied}
                    price={watch('price')}
                    taxedPrice={watch('taxedPrice')}
                  />
                </div>
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="offerPrice"
                  defaultValue={
                    product &&
                    product.offerPriceSetting &&
                    product.offerPriceSetting.enabled
                      ? product.offerPriceSetting.price
                      : ''
                  }
                  control={control}
                  rules={{
                    pattern: {
                      value: /^\d+(\.\d{1,5})?$/,
                      message: 'Ingrese un monto válido',
                    },
                    validate: value => {
                      const price = watch('price');
                      if (price === '' || value === '') return true;
                      else if (!isNaN(parseFloat(price))) {
                        const offerPrice = parseFloat(value);
                        if (offerPrice > price)
                          return 'Valor de oferta debe ser menor que valor de venta';
                        return true;
                      }
                      return true;
                    },
                  }}
                  render={({onChange, value}) => (
                    <CustomTextField
                      id="product-offer-price"
                      label={'Valor de oferta (Opcional)'}
                      autoComplete="product-offer-price"
                      error={!!errors.offerPrice}
                      helperText={
                        errors.offerPrice ? errors.offerPrice.message : ''
                      }
                      type="text"
                      value={value}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === '') setValue('taxedOfferPrice', '');
                        else if (!isNaN(Number(value)))
                          setValue('taxedOfferPrice', calcPriceWithTax(value), {
                            shouldValidate: false,
                            shouldDirty: false,
                          });
                        onChange(e);
                      }}
                      disabled={
                        isSaving ||
                        (storeHasProductVariantsFeature && variantsShow)
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="taxedOfferPrice"
                  defaultValue={
                    product &&
                    product.offerPriceSetting &&
                    product.offerPriceSetting.enabled
                      ? product.offerPriceSetting.price
                      : ''
                  }
                  control={control}
                  rules={{
                    pattern: {
                      value: /^\d+(\.\d{1,5})?$/,
                      message: 'Ingrese un monto válido',
                    },
                    validate: value => {
                      const price = watch('taxedPrice');
                      if (price === '' || value === '') return true;
                      else if (!isNaN(parseFloat(price))) {
                        const offerPrice = parseFloat(value);
                        if (offerPrice > price)
                          return 'Precio de oferta debe ser menor que precio de venta';
                        return true;
                      }
                      return true;
                    },
                  }}
                  render={({onChange, value}) => (
                    <CustomTextField
                      id="product-offer-price"
                      label={'Precio de oferta (Opcional)'}
                      autoComplete="product-offer-price"
                      error={!!errors.taxedOfferPrice}
                      helperText={
                        errors.taxedOfferPrice
                          ? errors.taxedOfferPrice.message
                          : ''
                      }
                      type="text"
                      value={value}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === '') setValue('offerPrice', '');
                        else if (!isNaN(Number(value)))
                          setValue('offerPrice', calcPriceWithoutTax(value), {
                            shouldValidate: false,
                            shouldDirty: false,
                          });
                        onChange(e);
                      }}
                      disabled={
                        !hasTaxesFeature ||
                        isSaving ||
                        (storeHasProductVariantsFeature && variantsShow)
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                  <ResumeTaxModal
                    taxes={taxesApplied}
                    price={watch('offerPrice')}
                    taxedPrice={watch('taxedOfferPrice')}
                  />
                </div>
              </Grid>
            </Grid>
          )}
          <div className={classes.desktopColumns}></div>
          {storeHasProductVariantsFeature && variantsShow && (
            <Typography className={classes.variantsActivatedPricesAlert}>
              <Info style={{marginRight: 4}} />
              Variantes activadas, debe configurar el precio para cada variante
            </Typography>
          )}
        </div>

        <ProductFormCategory
          product={product}
          onChange={handleChangeCategories}
          disabled={isSaving}
        />

        <ProductFeaturesSection
          control={control}
          productDescription={productDescription}
          setProductDescription={setProductDescription}
          enabledProductFeatures={enabledProductFeatures}
          setEnabledProductFeatures={setEnabledProductFeatures}
          errors={errors}
          disabled={!!isSaving}
          register={register}
          initialProductFeatures={initialProductFeatures}
        />

        {hasStockByProductFeature && (
          <div className={classes.contentSection}>
            <div className={classes.sectionTitle} id="product-stocks">
              Inventario {!hasStockByProductFeature && <PremiumFeatureBadge />}
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inventarioShow}
                  onChange={handleToggleInventario}
                  name="checkedI"
                  color="primary"
                />
              }
              className={classes.sectionCheckbox}
              label="Este producto tiene stocks limitados"
            />
            {inventarioShow && !variantsShow && (
              <CustomTextField
                id="product-stock"
                label="Stock"
                name="stock"
                autoComplete="product-stock"
                error={!!errors.stock}
                helperText={errors.stock ? errors.stock.message : ''}
                defaultValue={
                  product &&
                  product.stockSetting &&
                  product.stockSetting.enabled
                    ? product.stockSetting.stock
                    : ''
                }
                // type="text"
                inputRef={register({
                  required: 'Campo obligatorio',
                  pattern: {
                    value: /^[0-9]\d*$/,
                    message: 'Ingrese una cantidad válida',
                  },
                })}
                type="text"
                disabled={isSaving || variantsShow}
                // className={classes.productInputField}
              />
            )}
          </div>
        )}

        {premiumFeatures.filter(f => !f.hasFeature).length > 0 && (
          <div className={classes.contentSection}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography className={classes.sectionTitle}>
                Otras configuraciones
              </Typography>
              <Hidden xsDown>
                <div className={classes.onlyTiendadaProSection}>
                  <Lock className={classes.lockIcon} />
                  <Typography className={classes.onlyTiendadaPro}>
                    Sólo TiendaDA PRO
                  </Typography>
                  <CustomButton
                    className={classes.mejorarPlanButton}
                    to="/tiers-list"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Mejorar Plan
                  </CustomButton>
                </div>
              </Hidden>
            </div>
            <Divider className={classes.divider} style={{marginTop: 16}} />
            {premiumFeatures.map((feature, i) => {
              if (feature.hasFeature) return null;
              else
                return (
                  <div key={`otherFeature-${i}`}>
                    <div
                      className={classes.premiumFeaturesNameSection}
                      onClick={() =>
                        setPremiumFeatures(prev => {
                          const aux = [...prev];
                          aux[i].open = !aux[i].open;
                          return aux;
                        })
                      }
                    >
                      <Typography className={classes.premiumFeaturesName}>
                        {feature.name}
                      </Typography>
                      {feature.open ? <ExpandLess /> : <ExpandMore />}
                    </div>
                    <Collapse in={feature.open}>
                      <Typography
                        className={classes.premiumFeaturesDescription}
                      >
                        {feature.description + ' '}
                        {feature.link && (
                          <a
                            href={feature.link}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            Ver video
                          </a>
                        )}
                      </Typography>
                    </Collapse>
                    <Divider className={classes.divider} />
                  </div>
                );
            })}
            <Hidden smUp>
              <div className={classes.onlyTiendadaProSection}>
                <Lock className={classes.lockIcon} />
                <Typography className={classes.onlyTiendadaPro}>
                  Sólo TiendaDA PRO
                </Typography>
                <CustomButton
                  className={classes.mejorarPlanButton}
                  to="/tiers-list"
                >
                  Mejorar Plan
                </CustomButton>
              </div>
            </Hidden>
          </div>
        )}

        <TaxesSection
          taxesApplied={taxesApplied}
          allTaxes={storeTaxes}
          onSaveTax={tax => {
            handleChangeProductTax(tax.id);
            if (productId && productId !== 'new') fetchProduct({productId});
          }}
        />

        {(isMinimalPlan || storeTierName === 'TIER_310') && (
          <>
            <Hidden smDown>
              <img
                src={invoicingDesktop}
                alt="invoicing"
                className={classes.invoicingImage}
              />
            </Hidden>
            <Hidden mdUp>
              <img
                src={invoicingMobile}
                alt="invoicing"
                className={classes.invoicingImage}
              />
            </Hidden>
          </>
        )}
      </>
    );
  };

  // VARIANTS

  const handleOpenSelectionModal = (
    opt: ProductVariantOption,
    idx: number,
    variantsEval?: ProductVariantOptionValue[]
  ) => {
    const draftOption = {...opt};
    if (variantsEval) {
      draftOption.values = [];

      setTempSelectedValues(variantsEval);
    }

    setVariantOptionToEdit(draftOption);

    setOpenSelectionModal(true);
    setVariantOptionIndexEdit(idx);
  };

  const handleCloseSelectionModal = () => {
    setOpenSelectionModal(false);
    setTempSelectedValues([]);
    setVariantOptionToEdit(null);
  };

  const handleDeleteOptionValue = (valueIndex: number, index: number) => {
    const aux = [...productCriteria];
    const pcToEdit = {...aux[index]};
    const values = [...pcToEdit.variants];
    values.splice(valueIndex, 1);
    pcToEdit.variants = values;
    aux[index] = pcToEdit;
    setProductCriteria(aux);
    handleUpdateVariantsCombination(aux);
  };

  const disabledAddMoreVariantOption = useMemo(() => {
    const newIndexToAdd = productCriteria.length;
    if (newIndexToAdd === 0) return false;
    else {
      const prevCriteria = productCriteria[newIndexToAdd - 1];
      return prevCriteria?.variants?.length === 0;
    }
  }, [productCriteria]);

  const buildBulkPriceSection = () => {
    return (
      <div className={classes.contentSection}>
        <div className={classes.sectionTitle}>
          Tengo precios al por mayor
          {!hasBulkPriceFeature && <PremiumFeatureBadge />}
        </div>

        <Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={bulkPriceShow}
                onChange={handleBulkPriceShow}
                name="checkedB"
                color="primary"
                disabled={!hasBulkPriceFeature || bulkPriceDisabled}
              />
            }
            className={classes.sectionCheckbox}
            label="Al activar esta opcion puedes colocar precios de acuerdo a la cantidad
            de pedido"
          />
        </Typography>
        {bulkPriceShow && hasBulkPriceFeature && !bulkPriceDisabled ? (
          <>
            <TableContainer>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      colSpan={1}
                      style={{padding: 4, fontSize: '0.75rem'}}
                      align="left"
                    >
                      Desde
                    </TableCell>
                    <TableCell
                      colSpan={1}
                      style={{padding: 4, fontSize: '0.75rem'}}
                      align="left"
                    ></TableCell>
                    <TableCell
                      colSpan={1}
                      style={{padding: 4, fontSize: '0.75rem'}}
                      align="left"
                    >
                      Hasta
                    </TableCell>
                    <TableCell
                      colSpan={1}
                      align="left"
                      style={{
                        borderTop: 'none',
                        borderLeft: 'none',
                        borderRight: 'none',
                        padding: 4,
                        fontSize: 14,
                      }}
                      padding="none"
                      size="small"
                    >
                      <Hidden smDown>Valor por unidad</Hidden>
                      <Hidden mdUp>V/unidad</Hidden>
                    </TableCell>
                    <TableCell
                      colSpan={1}
                      align="left"
                      style={{
                        borderTop: 'none',
                        borderLeft: 'none',
                        borderRight: 'none',
                        padding: 4,
                        fontSize: 14,
                      }}
                      padding="none"
                      size="small"
                    >
                      <Hidden smDown>Precio por unidad</Hidden>
                      <Hidden mdUp>P/unidad</Hidden>
                    </TableCell>
                    <TableCell
                      colSpan={1}
                      style={{padding: 4, fontSize: '0.75rem'}}
                      align="left"
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkPriceFields.map((line, index) => (
                    <React.Fragment key={`line-${index}`}>
                      <TableRow key={index}>
                        <TableCell
                          component="th"
                          scope="row"
                          padding="none"
                          align="left"
                          style={{padding: 4}}
                        >
                          <CustomTextField
                            id="minimum-quantity"
                            size="small"
                            name={`bulkPriceLine.${index}.minQuantity`}
                            error={
                              errors.bulkPriceLine &&
                              errors.bulkPriceLine[index] &&
                              !!errors.bulkPriceLine[index].minQuantity
                            }
                            label="Cant."
                            defaultValue={line.minQuantity}
                            // value={line.minQuantity || ''}
                            type="text"
                            inputRef={register({
                              required:
                                'La cantidad no puede ser menor a 2 unidades',
                              validate: value => {
                                const getMinQuantity = (idx: number) =>
                                  watch(`bulkPriceLine.${idx}.minQuantity`);

                                if (index === 0) {
                                  if (parseInt(getMinQuantity(index)) < 2)
                                    return 'La cantidad no puede ser menor a 2 unidades';
                                }

                                if (
                                  parseInt(getMinQuantity(index)) <=
                                  parseInt(getMinQuantity(index - 1))
                                ) {
                                  return 'La cantidad debe ser mayor a la fila anterior';
                                } else return true;
                              },
                            })}
                            className={classes.quantityInputField}
                            // onChange={e => evaluateMinQuantity(e, index)}
                          />
                        </TableCell>
                        <TableCell
                          component="th"
                          align="center"
                          padding="none"
                          style={{
                            padding: 4,
                          }}
                        >
                          -
                        </TableCell>
                        {index === bulkPriceFields.length - 1 ? (
                          <TableCell
                            component="th"
                            padding="none"
                            align="left"
                            style={{
                              padding: 4,
                            }}
                          >
                            <div className={classes.quantityInputField}>
                              <Typography
                                variant="body2"
                                className={classes.more}
                              >
                                A más
                              </Typography>
                            </div>
                          </TableCell>
                        ) : (
                          <TableCell
                            component="th"
                            align="left"
                            padding="none"
                            style={{padding: 4}}
                          >
                            <CustomTextField
                              id="maximum-quantity"
                              size="small"
                              name="maximumQuantity"
                              autoComplete="maximumQuantity"
                              margin="none"
                              value={getBulkPriceMaxValue(index)}
                              type="text"
                              style={{marginTop: 0}}
                              disabled
                              className={classes.quantityInputField}
                            />
                          </TableCell>
                        )}
                        <TableCell component="th" align="left" padding="none">
                          <Controller
                            name={`bulkPriceLine.${index}.unitPrice`}
                            control={control}
                            defaultValue={line.unitPrice}
                            rules={{
                              required: bulkPriceShow,
                              pattern: {
                                value: /^\d+(\.\d{1,5})?$/,
                                message: 'Ingrese un monto válido',
                              },
                            }}
                            render={({onChange, value}) => (
                              <CustomTextField
                                fullWidth
                                defaultValue=""
                                id="unit-price"
                                size="small"
                                error={
                                  errors.bulkPriceLine &&
                                  errors.bulkPriceLine[index] &&
                                  !!errors.bulkPriceLine[index].unitPrice
                                }
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                value={value}
                                label="Precio"
                                type="text"
                                className={classes.bulkPriceInputField}
                                onChange={e => {
                                  const fieldName = `bulkPriceLine.${index}.taxedUnitPrice`;
                                  const value = e.target.value;
                                  if (value === '') setValue(fieldName, '');
                                  else if (!isNaN(Number(value)))
                                    setValue(
                                      fieldName,
                                      calcPriceWithTax(value)
                                    );
                                  onChange(e);
                                }}
                              />
                            )}
                          />

                          {/* <CustomTextField
                              fullWidth
                              defaultValue=""
                              id="unit-price"
                              size="small"
                              name={`bulkPriceLine.${index}.unitPrice`}
                              error={
                                errors.bulkPriceLine &&
                                errors.bulkPriceLine[index] &&
                                !!errors.bulkPriceLine[index].unitPrice
                              }
                              label="Precio"
                              type="text"
                              // value={line.unitPrice || ''}
                              inputRef={register({
                                required: bulkPriceShow,
                                pattern: {
                                  value: /^\d+(\.\d{1,5})?$/,
                                  message: 'Ingrese un monto válido',
                                },
                              })}
                              className={classes.bulkPriceInputField}
                              // onChange={e => {
                              //   const value = e.target.value;
                              //   if (!isNaN(Number(value)))
                              //     evaluateTaxedPrice(value, index, true);
                              //   evaluatePrice(e, index);
                              // }}
                            /> */}
                        </TableCell>
                        <TableCell component="th" align="left" padding="none">
                          <Controller
                            name={`bulkPriceLine.${index}.taxedUnitPrice`}
                            defaultValue={line.taxedUnitPrice}
                            control={control}
                            rules={{
                              required: bulkPriceShow,
                              pattern: {
                                value: /^\d+(\.\d{1,5})?$/,
                                message: 'Ingrese un monto válido',
                              },
                              validate: value =>
                                validateBulkPriceLineUnitPrice(value, index),
                            }}
                            render={({onChange, value}) => (
                              <CustomTextField
                                fullWidth
                                id="taxed-unit-price"
                                size="small"
                                error={
                                  errors.bulkPriceLine &&
                                  errors.bulkPriceLine[index] &&
                                  !!errors.bulkPriceLine[index].taxedUnitPrice
                                }
                                label="Precio"
                                type="text"
                                // value={line.taxedUnitPrice || ''}
                                // helperText={
                                //   errors.bulkPriceLine &&
                                //   errors.bulkPriceLine[index] &&
                                //   errors.bulkPriceLine[index].taxedUnitPrice
                                //     ? errors.bulkPriceLine[index].taxedUnitPrice
                                //         .message
                                //     : ''
                                // }
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                value={value}
                                disabled={!hasTaxesFeature}
                                className={classes.bulkPriceInputField}
                                onChange={e => {
                                  const unitPriceFieldName = `bulkPriceLine.${index}.unitPrice`;
                                  const value = e.target.value;
                                  if (value === '')
                                    setValue(unitPriceFieldName, '');
                                  else if (!isNaN(Number(value)))
                                    setValue(
                                      unitPriceFieldName,
                                      calcPriceWithoutTax(value),
                                      {
                                        shouldValidate: false,
                                        shouldDirty: false,
                                      }
                                    );
                                  onChange(e);
                                }}
                              />
                            )}
                          />

                          {/* <CustomTextField
                              fullWidth
                              id="taxed-unit-price"
                              size="small"
                              name={`bulkPriceLine.${index}.taxedUnitPrice`}
                              // error={
                              //   errors.bulkPriceLine &&
                              //   errors.bulkPriceLine[index] &&
                              //   !!errors.bulkPriceLine[index].taxedUnitPrice
                              // }
                              label="Precio"
                              type="text"
                              // value={line.taxedUnitPrice || ''}
                              // helperText={
                              //   errors.bulkPriceLine &&
                              //   errors.bulkPriceLine[index] &&
                              //   errors.bulkPriceLine[index].taxedUnitPrice
                              //     ? errors.bulkPriceLine[index].taxedUnitPrice
                              //         .message
                              //     : ''
                              // }
                              inputRef={register({
                                required: bulkPriceShow,
                                pattern: {
                                  value: /^\d+(\.\d{1,5})?$/,
                                  message: 'Ingrese un monto válido',
                                },
                              })}
                              disabled={!hasTaxesFeature}
                              className={classes.bulkPriceInputField}
                              // onChange={e => {
                              //   const value = e.target.value;
                              //   if (!isNaN(Number(value)))
                              //     evaluateTaxedPrice(value, index);
                              // }}
                            /> */}
                        </TableCell>
                        <TableCell
                          // component="div"
                          align="center"
                          padding="none"
                          className={classes.deleteIconCell}
                        >
                          <Button
                            className={classes.deleteIcon}
                            onClick={() => handleRemoveLine(index)}
                          >
                            <DeleteOutline />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {errors.bulkPriceLine &&
                        errors.bulkPriceLine[index] &&
                        errors.bulkPriceLine[index].minQuantity && (
                          <TableRow>
                            <TableCell colSpan={5} className={classes.errorRow}>
                              <p>
                                {
                                  errors.bulkPriceLine[index].minQuantity
                                    .message
                                }
                              </p>
                            </TableCell>
                          </TableRow>
                        )}
                      {errors.bulkPriceLine &&
                        errors.bulkPriceLine[index] &&
                        errors.bulkPriceLine[index].unitPrice && (
                          <TableRow>
                            <TableCell colSpan={5} className={classes.errorRow}>
                              <p>
                                {errors.bulkPriceLine[index].unitPrice.message}
                              </p>
                            </TableCell>
                          </TableRow>
                        )}

                      {errors.bulkPriceLine &&
                        errors.bulkPriceLine[index] &&
                        errors.bulkPriceLine[index].taxedUnitPrice && (
                          <TableRow>
                            <TableCell colSpan={5} className={classes.errorRow}>
                              <p>
                                {
                                  errors.bulkPriceLine[index].taxedUnitPrice
                                    .message
                                }
                              </p>
                            </TableCell>
                          </TableRow>
                        )}
                    </React.Fragment>
                  ))}
                  {/* {bulkPriceFields.map((line, index) => (
                      <React.Fragment key={`line-${index}`}>
                        <TableRow key={index}>
                          <TableCell
                            component="th"
                            scope="row"
                            padding="none"
                            align="left"
                            style={{padding: 4}}
                          >
                            <CustomTextField
                              id="minimum-quantity"
                              size="small"
                              name={`minimumQuantity-${index}`}
                              error={!!errors[`minimumQuantity-${index}`]}
                              label="Cant."
                              value={line.minQuantity || ''}
                              type="text"
                              inputRef={register({
                                required:
                                  'La cantidad no puede ser menor a 2 unidades',
                              })}
                              className={classes.quantityInputField}
                              onChange={e => evaluateMinQuantity(e, index)}
                            />
                          </TableCell>
                          <TableCell
                            component="th"
                            align="center"
                            padding="none"
                            style={{
                              padding: 4,
                            }}
                          >
                            -
                          </TableCell>
                          {index === bulkPriceLines.length - 1 ? (
                            <TableCell
                              component="th"
                              padding="none"
                              align="left"
                              style={{
                                padding: 4,
                              }}
                            >
                              <div className={classes.quantityInputField}>
                                <Typography
                                  variant="body2"
                                  className={classes.more}
                                >
                                  A más
                                </Typography>
                              </div>
                            </TableCell>
                          ) : (
                            <TableCell
                              component="th"
                              align="left"
                              padding="none"
                              style={{padding: 4}}
                            >
                              <CustomTextField
                                id="maximum-quantity"
                                size="small"
                                name="maximumQuantity"
                                autoComplete="maximumQuantity"
                                margin="none"
                                value={evaluateMaxQuantity(
                                  bulkPriceLines,
                                  index
                                )}
                                type="text"
                                style={{marginTop: 0}}
                                disabled={true}
                                className={classes.quantityInputField}
                              />
                            </TableCell>
                          )}
                          <TableCell component="th" align="left" padding="none">
                            <CustomTextField
                              fullWidth
                              id="unit-price"
                              size="small"
                              name={`unitPrice-${index}`}
                              error={!!errors[`unitPrice-${index}`]}
                              label="Precio"
                              type="text"
                              value={line.unitPrice || ''}
                              inputRef={register({
                                required: bulkPriceShow,
                                pattern: {
                                  value: /^\d+(\.\d{1,5})?$/,
                                  message: 'Ingrese un monto válido',
                                },
                              })}
                              className={classes.bulkPriceInputField}
                              onChange={e => {
                                const value = e.target.value;
                                if (!isNaN(Number(value)))
                                  evaluateTaxedPrice(value, index, true);
                                evaluatePrice(e, index);
                              }}
                            />
                          </TableCell>
                          <TableCell component="th" align="left" padding="none">
                            <CustomTextField
                              fullWidth
                              id="taxed-unit-price"
                              size="small"
                              name={`taxedUnitPrice-${index}`}
                              error={!!errors[`taxedUnitPrice-${index}`]}
                              label="Precio"
                              type="text"
                              value={line.taxedUnitPrice || ''}
                              inputRef={register({
                                required: bulkPriceShow,
                                pattern: {
                                  value: /^\d+(\.\d{1,5})?$/,
                                  message: 'Ingrese un monto válido',
                                },
                              })}
                              disabled={!hasTaxesFeature}
                              className={classes.bulkPriceInputField}
                              onChange={e => {
                                const value = e.target.value;
                                if (!isNaN(Number(value)))
                                  evaluateTaxedPrice(value, index);
                              }}
                            />
                          </TableCell>
                          <TableCell
                            // component="div"
                            align="center"
                            padding="none"
                            className={classes.deleteIconCell}
                          >
                            <Button
                              className={classes.deleteIcon}
                              onClick={() => handleRemoveLine(index)}
                            >
                              <DeleteOutline />
                            </Button>
                          </TableCell>
                        </TableRow>
                        {errors[`minimumQuantity-${index}`] && (
                          <TableRow>
                            <TableCell colSpan={5} className={classes.errorRow}>
                              <p>
                                {errors[`minimumQuantity-${index}`].message}
                              </p>
                            </TableCell>
                          </TableRow>
                        )}
                        {errors[`unitPrice-${index}`] && (
                          <TableRow>
                            <TableCell colSpan={5} className={classes.errorRow}>
                              <p>{errors[`unitPrice-${index}`].message}</p>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))} */}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomButton
              onClick={handleAddLine}
              className={classes.addLineButton}
              startIcon={<AddCircleTwoTone />}
            >
              Añadir otra fila precio
            </CustomButton>
          </>
        ) : null}
      </div>
    );
  };
  const aspectRatio =
    hasImageShapeFeature && productImageShape === 'RECTANGLE' ? 0.75 : 1;

  if (isFetching) return <LoadingSpinner />;

  return (
    <>
      <CustomExitPrompt
        when={
          isDirtyForm &&
          !editProductSuccess &&
          !createProductSuccess &&
          !deleteProductSuccess &&
          !isInstagramUpload
        }
      />
      <SnackBar
        mode="error"
        message={bulkErrorMessage}
        handleClose={() => setBulkErrorMessage('')}
      />
      <SnackBar
        mode="error"
        message={variantSkuErrorsSnack ? 'Error en SKU de variantes' : ''}
        handleClose={() => setVariantSkuErrorsSnack(false)}
      />
      <SnackBar
        mode="info"
        message={
          bulkPriceOfferPriceAlert
            ? 'Para activar esta opción, la variante no puede tener precio de oferta'
            : ''
        }
        handleClose={() => setBulkPriceOfferPriceAlert(false)}
      />

      <div className={classes.formOverlay}></div>

      {!isFetching && productImageShape && imagesToCrop.length > 0 && (
        <CropModal
          open
          aspect={aspectRatio}
          onClose={() => {
            setImagesToCrop([]);
          }}
          images={imagesToCrop}
          compressOptions={{
            quality: 0.8,
            maxWidth: 2160,
            maxHeight: 2160 / aspectRatio,
            threshold: 1024 * 1024,
          }}
          keepFormat
          onSubmit={results => {
            setImagesToCrop([]);
            const indexToEdit = imageIndexToCropRef.current;

            if (
              product &&
              isDuplicating &&
              !hasVariantsNewImagesAlertRef.current
            ) {
              if (product.productVariantSetting.variants?.length) {
                hasVariantsNewImagesAlertRef.current = true;
                showAlert(
                  'Has subido nuevas imágenes. Recuerda revisar y actualizar las imágenes de las variantes si es necesario.',
                  {
                    type: 'warning',
                    duration: 15000,
                  }
                );
              }
            }

            if (typeof indexToEdit !== 'number') {
              return setCropResult(prev => [...prev, ...results]);
            }

            setCropResult(prev => {
              const aux = [...prev];
              aux[indexToEdit] = results[0];
              return aux;
            });
          }}
        />
      )}

      {(isSaving || isFetching || isFetchingTaxSetting || loading) && (
        <LoadingSpinner />
      )}
      <main className={classes.content}>
        <TabWrapper tabSelected={tabSelected} tab="variants">
          <VariantsSection
            classes={classes}
            productCriteria={productCriteria}
            variantsShow={variantsShow}
            storeHasProductVariantsFeature={storeHasProductVariantsFeature}
            userHasProductVariantsFeatureDisabled={false}
            fetchProductVariantOptions={fetchProductVariantOptions}
            openSelectionModal={openSelectionModal}
            variantOptionToEdit={variantOptionToEdit}
            variantOptionIndexEdit={variantOptionIndexEdit}
            tempSelectedValues={tempSelectedValues}
            handleOpenSelectionModal={handleOpenSelectionModal}
            handleCloseSelectionModal={handleCloseSelectionModal}
            handleChangeCriteriaVariants={handleChangeCriteriaVariants}
            handleAddCriteria={handleAddCriteria}
            handleRemoveCriteria={handleRemoveCriteria}
            handleDeleteOptionValue={handleDeleteOptionValue}
            handleVariantsShow={handleVariantsShow}
            globalProductVariantOptions={globalProductVariantOptions}
            store={store}
            getBasicDomain={getBasicDomain}
            variantsTable={variantsTable.map(v => ({
              ...v,
              taxedPrice: v.taxedPrice || '',
              enabled: !!v.enabled,
            }))}
            calcPriceWithTax={calcPriceWithTax}
            calcPriceWithoutTax={calcPriceWithoutTax}
            enabledTaxInclusivePricing={enabledTaxInclusivePricing}
            handleChangeTaxedVariantPrice={handleChangeTaxedVariantPrice}
            handleChangeVariantPrice={handleChangeVariantPrice}
            handleChangeVariantTaxedOfferPrice={
              handleChangeVariantTaxedOfferPrice
            }
            handleChangeVariantOfferPrice={handleChangeVariantOfferPrice}
            handleChangeVariantBulkPrice={handleChangeVariantBulkPrice}
            inventarioShow={inventarioShow}
            handleToggleInventario={handleToggleInventario}
            handleChangeVariantStock={handleChangeVariantStock}
            handleChangeVariantSKU={handleChangeVariantSKU}
            handleToggleVariantsRow={handleToggleVariantsRow}
            allowMixNMatch={allowMixNMatch}
            handleAllowMixNMatch={handleAllowMixNMatch}
            disabledAddMoreVariantOption={disabledAddMoreVariantOption}
            bulkPriceShow={bulkPriceShow}
            cropResult={cropResult}
            handleVariantImageModalOpen={handleVariantImageModalOpen}
            isSaving={isSaving}
            errors={errors}
            variantSkuErrors={variantSkuErrors}
            validateVariantSku={validateVariantSku}
            setInventarioShow={setInventarioShow}
          />
        </TabWrapper>
        <TabWrapper tabSelected={tabSelected} tab="sections">
          <Sections product={product} />
        </TabWrapper>
        <TabWrapper
          tabSelected={tabSelected}
          tab="bulkprice"
          disabled={bulkPriceDisabled}
        >
          {buildBulkPriceSection()}
        </TabWrapper>
        {/* <TabWrapper tabSelected={tabSelected} tab="options"> */}
        <div
          style={{
            display: tabSelected === 'options' ? undefined : 'none',
          }}
        >
          {console.log({isFetching, productId})}
          <Options
            onChange={handleChangeProductOptionSetting}
            productId={productId}
          />
        </div>
        {/* </TabWrapper> */}
        <TabWrapper tabSelected={tabSelected} tab="relatedProducts">
          <RelatedProducts disabled={!!isSaving} />
        </TabWrapper>
        <TabWrapper tabSelected={tabSelected} tab="seo">
          <Seo disabled={!!isSaving} />
        </TabWrapper>

        <TabWrapper tabSelected={tabSelected} tab="general">
          {buildMainContent()}
        </TabWrapper>
      </main>
      <Hidden xsDown>
        <div className={classes.bottomBar}>
          {!isFromExternalResource && (
            <div className={classes.bottomBarContent}>
              <div>
                {!isInstagramUpload ? (
                  <Typography className={classes.bottomBarText}>
                    {productId && productId !== 'new'
                      ? product
                        ? `${product.name}${
                            isDirtyForm ? ' - Producto sin Guardar' : ''
                          }`
                        : ''
                      : `Producto sin Guardar`}
                  </Typography>
                ) : (
                  <CustomButton
                    onClick={instagramUploadEarlyExit}
                    disabled={isSaving}
                  >
                    Salir
                  </CustomButton>
                )}
                {tabWithErrors?.length > 0 && (
                  <div className={classes.tabErrors}>
                    <InfoOutlined className={classes.tabErrorsIcon} />
                    <Typography className={classes.tabErrorsName}>
                      Error: Revisar las pestañas de:{' '}
                      {tabWithErrors.map(el => el.name).join(', ')}
                    </Typography>
                  </div>
                )}
              </div>

              <CustomButton
                variant="contained"
                className={classes.bottomBarButton}
                onClick={() => {
                  if (isDowngradeTier) {
                    toggleShowVariantBulkPriceAlertModal();
                  } else {
                    handleSubmit(handleEditSubmit)();
                  }
                }}
                disabled={isSaving}
              >
                {!isInstagramUpload
                  ? productId !== 'new'
                    ? 'Guardar'
                    : 'Añadir Producto'
                  : 'Siguiente'}
              </CustomButton>
            </div>
          )}
          {isFromExternalResource && (
            <div className={classes.bottomBarContent}>
              <div>
                Este producto fue creado desde una fuente externa y no puede ser
                editado
              </div>
            </div>
          )}
        </div>
      </Hidden>
      <Hidden smUp>
        <CustomButton
          variant="contained"
          className={classes.mobileBottomButton}
          onClick={() => {
            if (isDowngradeTier) {
              toggleShowVariantBulkPriceAlertModal();
            } else {
              handleSubmit(handleEditSubmit)();
            }
          }}
          disabled={isSaving}
        >
          {!isInstagramUpload
            ? productId !== 'new'
              ? 'Guardar'
              : 'Añadir Producto'
            : 'Siguiente'}
        </CustomButton>
      </Hidden>
      <StoreChurnModal
        open={showVariantBulkPriceAlertModal}
        handleClose={() => toggleShowVariantBulkPriceAlertModal()}
      />
      <VariantBulkPriceAlertModal
        open={variantBulkPriceAlertOpen}
        bulkPrices={watch('bulkPriceLine')}
        currency={currency}
        handleClose={() => setVariantBulkPriceAlertOpen(false)}
        handleConfirm={() =>
          setVariantsTable(prev => {
            let aux = [...prev];
            aux[rowToChangeRef.current].bulkPrice = valueToChangeToRef.current;
            return aux;
          })
        }
      />
      <ChooseVariantImageModal
        open={variantImageModalOpen}
        handleClose={handleVariantImageModalClose}
        editImages={savedImages}
        newEditImages={cropResult.map(({preview}) => preview)}
        onUploadImage={files => {
          setImagesToCrop(
            files.map(file => ({
              preview: URL.createObjectURL(file),
              file,
            }))
          );
        }}
        handleRemoveNewImage={index => {
          handleRemoveNewImage(index);
        }}
        uploadDisabled={uploadDisabled}
        handleSetVariantImage={handleSetVariantImage}
        defaultVariantImage={defaultVariantImage}
        multiple={hasMultipleImagesFeature}
      />
      <TierRestrictionModal
        open={tierRestrictionModal}
        handleClose={() => setTierRestrictionModal(false)}
      />
    </>
  );
}
